import { createStore } from "zustand/vanilla";
import {
  DEFAULT_GAME_STATUSES,
  LOBBY_GAME_ORDER,
  isHiddenGame,
  type AvatarUploadResult,
  type DmLastMessage,
  type FriendLists,
  type GameActivation,
  type GameCounts,
  type GameId,
  type GameInviteView,
  type GateChannel,
  type InboxMessageView,
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type MaintenanceWarning,
  type MatchHistoryItem,
  type PresenceGameId,
  type UpdateProfileInput,
  type UsernameAvailability,
  type UserStats,
} from "@bing/shared";
import { ApiError, isUnchanged, type Unchanged } from "../api/client";
import {
  AVATAR_UPLOAD_RETRY,
  isTransientBootError,
  resilientGetMe,
  resilientRequest,
} from "../api/resilient-boot";
import type {
  AcceptGameInviteResponse,
  ActiveGame,
  ApiClient,
  FriendView,
  InviteUser,
  Profile,
} from "../api/client";
import { GAMES, ONLINE_GAME_IDS } from "../config";
import type { TableOnScreen } from "../game/crash-return";
import {
  DEFAULT_TABLE_SETTINGS,
  parseTableCode,
  QUICK_MATCH,
  setBotUsername,
} from "../game/private-table";
import { reportClientFault } from "../game/client-report";
import { resolveLaunchContext, type LaunchContext } from "../telegram/launch";
import {
  anchorInvites,
  CLOCK_SKEW_REPORT_MS,
  deviceClockSkewMs,
  isLiveInvite,
  type ClientInvite,
} from "../game/invite-clock";
import { BROADCAST_RESYNC_SPREAD_MS } from "./badge-poll";
import {
  backoffDelayMs,
  lifecycle,
  MAX_RECONNECT_ATTEMPTS,
  RECONNECT_WINDOW_MS,
  SERVER_GRACE_MARGIN_MS,
} from "../game/lifecycle";
import { onSavedGameRetired } from "../game/fresh-solo";
import { clearOnlineGame } from "../game/online-resume";
import { refusalWayBack, straightBackTarget } from "../game/straight-back";
import { setResumeSolo, takeResumeIntent, takeResumeSolo } from "../game/fresh-solo";
import type { ExitIntent, OnlineJoin, TableSettings } from "../game/private-table";
import type {
  FriendEventPush,
  PresenceConnector,
  PresenceHandle,
  PresenceStatus,
} from "../game/presence";
import { fa } from "../i18n/fa";

export type SessionStatus = "idle" | "loading" | "ready" | "error";
export type View =
  | "lobby"
  | "profile"
  | "userProfile"
  | "game"
  | "gameSettings"
  | "friends"
  | "chat"
  | "leaderboard"
  | "store"
  | "settings"
  | "blockedUsers"
  | "help"
  | "history"
  | "messages";

export interface SessionState {
  status: SessionStatus;
  profile: Profile | null;
  error: string | null;
  /**
   * Which action, if any, the boot error screen should offer beside {@link SessionState.error} —
   * «تلاش مجدد» (re-ask), «باز کردن دوباره» (load the app afresh), or nothing at all.
   *
   * It is {@link bootErrorAction} of the SAME failure `error` was built from; see that function
   * for the rule and the three cases that shaped it. Written in the SAME `set()` as `error`, so
   * no frame ever shows one without the other.
   */
  errorAction: BootErrorAction;
  view: View;
  /**
   * MAINTENANCE — the platform is being updated and this user is not an admin.
   *
   * Set from the api client when ANY call returns 503 `MAINTENANCE`, and cleared on the next
   * SUCCESSFUL response, so the app comes back by itself once maintenance ends (no reload).
   * The `message` is the admin's own Persian text from the panel.
   *
   * Purely a mirror of the SERVER's decision — it is never trusted as a gate. Every request
   * and every game join is refused server-side regardless of what this holds.
   */
  maintenance: { active: boolean; message: string } | null;
  setMaintenance: (state: { active: boolean; message: string }) => void;
  /**
   * MANDATORY-CHANNEL GATE — the user must join these channels before using the mini app (same
   * `RequiredChannel` config as the bot's `/start` gate). Set from the api client on a 403
   * `CHANNELS_REQUIRED` (the channels come from the error `details`), cleared on the next
   * successful response (so «عضو شدم» dismisses it once the user has joined). A server-side mirror,
   * never trusted as the gate — every request is re-checked server-side.
   */
  channelGate: GateChannel[] | null;
  setChannelGate: (channels: GateChannel[] | null) => void;
  /** Which game's room the "game" view renders (Section: lobby tile → table). */
  activeGame: string;
  /** Whether the active game is an online multiplayer match (vs. the solo/bots room). */
  online: boolean;
  /** How the online container acquires its room: quick-match, host-create, or join-by-id. */
  onlineJoin: OnlineJoin;
  /**
   * The Colyseus roomId of the table we are CONNECTED to right now, or null.
   *
   * ⚠️ This exists because the join INTENT cannot answer "which table am I at?". `onlineJoin`
   * carries a roomId only for `kind:"join"` — a quick-matcher's is `{kind:"quick"}`, and the
   * room it lands in is chosen by the matchmaker. Every invite guard that asks "am I already
   * there?" must read THIS, not the intent (see {@link joinedRoomId}).
   *
   * Written by `OnlineGame` from the connector's `onLive` (fires on connect AND every
   * reconnect, for every join kind) and cleared when the container unmounts.
   */
  liveRoomId: string | null;
  /** Record / clear the connected table's roomId (see {@link SessionState.liveRoomId}). */
  setLiveRoom: (roomId: string | null) => void;
  /**
   * THE TABLE ON SCREEN, FOR THE CRASH SCREEN'S DOOR (card-a5 item 3) — what the mounted `OnlineGame`
   * is sitting at, read when the fallback renders: its seat as the connector last reported it (in
   * memory only — never the strip's ticket), its room while still connecting, dealt or not, and
   * whether the server holds the seat after a drop. Registered by `OnlineGame`'s committed mount; KEPT
   * through a crash (the door needs it, and a crash that recurs before the next table commits needs it
   * again); released on every other unmount. Null = no table on screen.
   */
  tableOnScreen: (() => TableOnScreen) | null;
  setTableOnScreen: (provider: () => TableOnScreen) => void;
  /** Release it — only if it is still THIS provider (the next table's mount may have replaced it). */
  releaseTableOnScreen: (provider: () => TableOnScreen) => void;
  /**
   * Are we seated at a PUBLIC online table that has dealt and not yet finished?
   *
   * ⚠️ THE ONE THING THIS MEANS: leaving right now costs a recorded loss (2026-08-27). It is
   * therefore false in a waiting room, false on a private table, false on the win screen and
   * false in a solo game — leaving any of those is free.
   *
   * It exists so the app-level invite prompt can REFUSE an invite outright instead of offering a
   * «پیوستن» that would quietly leave a public game through the old resumable path. `InvitePrompt`
   * lives at the root and cannot see the table's own lobby frame; `OnlineGame` can, so it publishes
   * the one derived boolean here and clears it on unmount (the {@link SessionState.liveRoomId}
   * pattern).
   */
  publicGameInProgress: boolean;
  /** Publish / clear {@link SessionState.publicGameInProgress}. */
  setPublicGameInProgress: (value: boolean) => void;
  /** Raw Telegram initData, for authenticating the game-server WS connection. */
  initDataRaw: string | null;
  /** The API client, exposed so feature screens (friends, chat) can call it
   * directly without threading it through props or another provider. */
  api: ApiClient;
  /** The friend whose DM thread is open (null → show the conversation picker). */
  chatPeer: FriendView | null;
  /** The user whose PROFILE the "userProfile" view shows, + the view to return to on back. */
  viewedUserId: string | null;
  profileReturnView: View;

  // ── Profile slice ──
  /** Aggregate + per-game stats (seeded from `GET /me`, refreshable via `GET /me/stats`). */
  stats: UserStats | null;
  /** Loaded match-history rows (grows as `loadMoreHistory` pages through). */
  history: MatchHistoryItem[];
  /** Cursor for the next history page, or null when the first page hasn't loaded / none remain. */
  historyCursor: string | null;
  /** Whether the first history page has been fetched (drives the empty-vs-loading state). */
  historyLoaded: boolean;
  /** True while a history page (first or subsequent) is in flight. */
  historyLoading: boolean;
  /** Last history-load error message, or null. */
  historyError: string | null;
  /** The user's resumable in-progress games (Home "continue playing" strip). */
  activeGames: ActiveGame[];
  /** True while the first active-games fetch is in flight. */
  activeGamesLoading: boolean;

  // ── Friends slice ──
  /** Accepted friends. */
  friends: FriendView[];
  /** Incoming pending requests (drive the sub-tab + bottom-nav badges). */
  incoming: FriendView[];
  /** Outgoing pending requests. */
  outgoing: FriendView[];
  /** `incoming.length`, hoisted so the badge can subscribe to just the number. */
  incomingCount: number;
  /** True while the first (or a manual retry) list refresh is in flight. */
  friendsLoading: boolean;
  /** Last list-refresh error message, or null. */
  friendsError: string | null;
  /**
   * «کاربران مسدود» — everyone the caller has blocked, newest first. `null` until the
   * screen has loaded it once, so the screen shows a spinner rather than flashing the
   * "you haven't blocked anyone" line at somebody who has.
   *
   * NOT part of the ~20s friends poll: it is fetched when the screen opens (and after an
   * unblock, from the local drop below) and nowhere else.
   */
  blocked: FriendView[] | null;
  /** True while a blocked-list fetch is in flight. */
  blockedLoading: boolean;
  /** Last blocked-list error message, or null (an older api answers 404 here). */
  blockedError: string | null;

  // ── «برترین‌ها» slice ──
  /** The board currently on screen, or null before the first successful read. */
  leaderboard: LeaderboardResponse | null;
  /** True while a board fetch is in flight and there is nothing to show yet. */
  leaderboardLoading: boolean;
  /** Last board error, or null. */
  leaderboardError: string | null;
  /**
   * Where «برترین‌ها» was standing when the viewer left it to look at somebody's profile, or null.
   *
   * ⚠️ CONSUMED ONCE, exactly like `takeResumeIntent()`. It is NOT a persistent screen state: the
   * app shell re-mounts every screen on a view change specifically so each tab opens at its own
   * top rather than inheriting the last one's offset (see `ViewFade`), and a board that restored
   * its filter and scroll on EVERY mount would quietly contradict that. What the round trip to a
   * profile owes is that Back puts you back — nothing wider.
   */
  leaderboardReturn: { period: LeaderboardPeriod; game: GameId | "all"; scrollTop: number } | null;

  // ── DM-unread slice (per-thread counts + the چت tab badge) ──
  /** Unread DM count per peer app-user id (absent/0 = fully read). Seeded from
   * `GET /me/dm-unread`, incremented live by `dm_message` pushes, zeroed the
   * moment that peer's thread opens (the DM room persists the same clear). */
  dmUnread: Record<string, number>;
  /** Sum of {@link dmUnread} — the bottom-nav چت badge subscribes to just this. */
  dmUnreadTotal: number;
  /** Last-message preview per peer app-user id (`GET /me/dm-summary`) — the
   * chat list renders it instead of the tap-to-chat placeholder. Refreshed with
   * the unread counts (same fetch) and after each live `dm_message` push. */
  dmPreviews: Record<string, DmLastMessage>;
  /**
   * peerId → the peer's leak-free identity, straight from `/me/dm-summary`.
   *
   * This is what lets the چت list render a conversation whose peer is NOT (or is no longer) a
   * friend. The list used to be `friends.filter(hasConversation)`, so an unfriended peer took the
   * whole thread off the screen while the server kept serving it — measured on prod 2026-08-25:
   * 1,201 conversations, 781 users. Empty for a row an older api sent without `peer`; the list
   * then falls back to the friend entry, i.e. exactly the old behaviour.
   */
  dmPeers: Record<string, InviteUser>;
  /**
   * Peer app-user ids the CALLER has blocked, from `/me/dm-summary`'s `blockedByMe`.
   *
   * ⚠️ THE CLIENT CANNOT DERIVE THIS. A blocked peer is in none of `/friends/all`'s three arrays,
   * so a row rendered from `dmPeers` has `status: "none"` and the thread would draw a live
   * composer over a send the server refuses. A blocked THREAD is served to both parties now (the
   * disappearance was the tell), which is exactly why the marker has to travel with it.
   *
   * ⚠️ THERE IS NO CONVERSE, AND THERE MUST NOT BE. Nothing tells a client that the PEER blocked
   * THEM — that is the disclosure this whole change removes.
   */
  dmBlocked: Record<string, true>;

  // ── In-app inbox slice (@bing broadcasts) ──
  /** The caller's inbox messages (newest first); loaded on the Messages screen. */
  inbox: InboxMessageView[];
  /** Unread inbox count — drives the menu badge; refreshed on boot + the ~20s poll. */
  inboxUnread: number;

  // ── Game-invites slice (friend → private table; 60s TTL) ──
  /** Live invites addressed to me (PENDING, unexpired, newest first), each carrying the
   *  device-clock deadline anchored when it arrived — see `game/invite-clock.ts`. */
  incomingInvites: ClientInvite[];
  /** Live invites I sent (plus locally-reconciled terminal states from pushes). */
  outgoingInvites: GameInviteView[];
  /** The incoming invite currently shown as the app-level prompt overlay. */
  invitePrompt: ClientInvite | null;
  /** The full-screen "switching game" transition overlay (accepting an invite while already in a
   *  game): a Persian message shown with motion while the current game is left server-side and the
   *  invited one is joined. Null = no switch in progress. */
  gameSwitch: { message: string } | null;
  /**
   * A friend picked on the Friends screen to auto-invite once THE TABLE THAT PICK CREATES reports
   * its Colyseus roomId (consumed by that table's waiting-room invite panel — see
   * {@link pendingInviteHere}).
   *
   * ⚠️ BOUND TO ITS OWN CREATE (card-a5 item 15). `join` is the very `onlineJoin` object
   * `inviteFriendToGame` set, compared by IDENTITY: a table created later with the same default
   * rules is EQUAL in value and is a different table. A create that never produced a table
   * (refused, failed, abandoned) leaves the marker behind, and bound it can never fire anywhere
   * else — not where the player is crowned, not in a rematch successor they own, not at a table
   * they create later. It was gated on the viewer's ROLE (`isHost`), and hosting a table is not
   * having created it for the pick.
   */
  pendingInvite: { toUserId: string; join: OnlineJoin } | null;

  // ── Games slice (lobby activation + live per-game counts) ──
  /** The lobby's game list in canonical order. Seeded from the static catalogue
   * (the pre-load fallback) and replaced by the `GET /games` server truth. */
  lobbyGames: GameActivation[];
  /** Whether the caller is an admin — COMING_SOON tiles stay playable for admins
   * (the game-server enforces the same bypass on join). */
  isAdmin: boolean;
  /** Live per-game in-game player counts, pushed over the presence socket
   * (`game-counts`: a snapshot on join, then throttled updates). Zero-filled — except «مار»'s
   * `snake`, present only while the game-server counts it (absent = no count on its tile). */
  gameCounts: GameCounts;
  /** TOTAL users online right now (per-node), from the same `game-counts`
   * pushes — the lobby top bar's centre counter. 0 = unknown/disconnected. */
  onlinePlayers: number;
  /** Bumped whenever the saved online-resume game is invalidated out-of-band (a
   * `game_replaced` push — the owner filled your reclaimable seat with a friend). The
   * lobby's «ادامه بازی» strip re-reads localStorage on a change so the row drops LIVE. */
  resumeStripVersion: number;

  // ── Presence slice ──
  /** Friends' live presence keyed by userId ("online" | "in_game"; absent = offline). */
  presence: Record<string, PresenceStatus>;
  /**
   * Is the presence socket CONNECTED right now?
   *
   * ⚠️ IT IS A CADENCE SWITCH, NOT A UI FLAG — nothing renders it. While it is true every badge
   * has a live push behind it and the client keeps only a slow safety net; the moment it goes
   * false there are no pushes at all, so the client falls back to the original 20 s interval.
   * It starts FALSE, deliberately: before the first socket lands, the fast poll is the truth.
   */
  presenceLive: boolean;
  /** Transient app-wide toast for a pushed friend/invite/DM event. */
  friendToast: string | null;
  /** The invite id a `game_invite` toast points at (tap → open the prompt), or null. */
  friendToastInvite: string | null;
  /** Where a non-invite toast tap navigates: the چت tab for a DM push, else دوستان. */
  friendToastNav: "friends" | "chat";
  /**
   * PRE-DEPLOY MAINTENANCE WARNING (deploy-safety) — set from the presence socket's `maintenance`
   * push (an admin fired it before a deploy). App.tsx renders an app-wide apology modal while it's
   * non-null; «متوجه شدم» dismisses it client-side (no server round-trip). It does NOT gate anything
   * — a heads-up only. `etaMinutes` is an optional hint carried on the push.
   */
  maintenanceWarning: MaintenanceWarning | null;
  /** Dismiss the maintenance-warning modal (the «متوجه شدم» tap). */
  dismissMaintenanceWarning: () => void;

  setView: (view: View) => void;
  /** Where a drawer-level screen (Settings / History) returns on BACK — set by whoever
   * opened it («profile» from Profile's nav buttons, «lobby» from the hamburger drawer),
   * so back goes to the origin instead of always the lobby. Default `"lobby"`. */
  pageReturn: View;
  setPageReturn: (view: View) => void;
  /** A leave-guard registered by a screen with unsaved edits (Profile): called with the
   * NEXT view before `setView` applies it. Return false to BLOCK the navigation — the
   * guard shows its own confirm and drives the nav itself (discard/save). Catches EVERY
   * `setView`-based exit (in-app back, bottom nav, the Telegram back button which routes
   * through setView). Cleared automatically once a navigation goes through. */
  navGuard: ((next: View) => boolean) | null;
  setNavGuard: (guard: ((next: View) => boolean) | null) => void;
  /**
   * AN IN-APP WAY OFF A PUBLIC TABLE IN PROGRESS, WAITING FOR THE «خروج» CONFIRMATION (card-a5 item 1).
   *
   * The operator: «being active in the app during the hold without returning counts exactly like
   * «خروج»». Leaving the table's screen in-app without «خروج» (a toast's tap, the reconnect overlay's
   * exit) is exactly that act, so `setView` does not take it: it parks the destination here, and the
   * table (`OnlineGame`) shows the SAME confirmation «خروج» shows — confirm leaves the way «خروج»
   * leaves (charged the same) and then goes there; cancel stays. Null = nothing pending.
   */
  pendingExit: { to: View } | null;
  /** Drop a pending exit (the player chose to stay at the table). */
  cancelTableExit: () => void;
  /**
   * Leave the game view WITHOUT the guard above — for the table's own deliberate exits (after
   * «خروج», a kick, an idle removal, a closed table) and the free ones (a failed reconnect, a seat
   * already gone). Never a way around the confirmation: every caller is past it or owes none.
   */
  leaveTable: (view?: View) => void;
  /** Do what a routed act elsewhere was about to do (card-a5 item 1) — see {@link ExitIntent}. */
  runExitIntent: (intent: ExitIntent) => void;
  setActiveGame: (id: string) => void;
  /** Enter the online multiplayer match for `id` via public quick-match (→ table). */
  playOnline: (id: string) => void;
  /** Open the host's Game Settings screen for `id` (pre-create private table). */
  openGameSettings: (id: string) => void;
  /** Host-create a private table for `id` with `settings` (→ waiting room). */
  /** `seats` — the size the host picked, for a game whose sizes are separate room types (پاسور);
   *  rides the create intent, never the settings (see `OnlineJoin`). Omitted ⇒ the default size. */
  createPrivate: (id: string, settings: TableSettings, seats?: number) => void;
  /** Join an existing table for `id` by its Colyseus `roomId` (→ waiting room). `preferredSeat`
   *  is the invited chair, honoured server-side only while it is still free. */
  joinPrivate: (id: string, roomId: string, preferredSeat?: number) => void;
  /** RE-JOIN a live online game we were in — reconnect with the saved token and reclaim our seat
   *  from the bot (same-device «ادامه بازی»). A locked, in-progress room refuses a fresh join.
   *  `auto` marks the app's own STRAIGHT-BACK return (card-a5 item 1): its failure is silent. */
  rejoinOnline: (
    game: string,
    roomId: string,
    token: string,
    opts?: { auto?: "table" | "waiting" },
  ) => void;
  /**
   * STRAIGHT BACK TO A HELD PUBLIC SEAT (card-a5 item 1) — what the app does on open, before any
   * other screen: when this device holds a seat at a public table in progress (or in a public waiting
   * room, inside the server's 120 s hold), enter it as an `auto` rejoin and say so (`true`). Nothing
   * held ⇒ `false`, and nothing changes. See `game/straight-back.ts`.
   */
  returnToHeldTable: () => boolean;
  /**
   * BACK TO THE SEAT THE GAME-SERVER NAMED (card-9f) — the «بازی عمومی» refusal's «بازگشت به بازی» (and,
   * with `then`, its «خروج»: back first, then the table's own «خروج» confirmation). Through THIS device's
   * ticket when it names that room (today's door), otherwise `token: null` — the game-server's held-seat
   * door. Sets the join itself: it IS the return, never an act elsewhere (`viaHeldTable` must not see it).
   */
  returnToHeldSeat: (game: string, roomId: string, then?: ExitIntent) => void;
  /** RESUME a persisted ONLINE chess game from the DB (cross-device «ادامه بازی») — join
   *  `chess-online` by the stable `onlineGameId` (filterBy), rehydrating the game for both players. */
  resumeOnlineGame: (game: string, onlineGameId: string) => void;
  /** If `startParam` is a `t_<game>_<roomId>` deep link, join that table by id
   * (returns whether it was handled). An `f_<telegramId>` FOLLOW deep link (the
   * bot's «دعوت دوستان» flow) is stashed instead — a friend request to the
   * inviter fires once the profile loads (see `pendingFollowId`). */
  routeFromStartParam: (startParam: string | null | undefined) => boolean;
  /** Inviter ref from an `f_<ref>` start param (their OPAQUE app id — cuid — or a
   * legacy numeric Telegram id from old links), consumed on boot. */
  pendingFollowId: string | null;
  /** Peer app id from a `c_<appUserId>` start param — the button on the offline "new
   * message" Telegram PV. Consumed on boot: the DM thread opens once friends are loaded. */
  pendingChatPeerId: string | null;
  /** Open a DM thread with `peer` and switch to the chat view. */
  openChat: (peer: FriendView) => void;
  setChatPeer: (peer: FriendView | null) => void;
  /** Open another user's profile view (remembers the current view for the back button). */
  openUserProfile: (userId: string) => void;
  loadProfile: () => Promise<void>;
  /**
   * Re-read the player's standing IN THE BACKGROUND — what a `session` push runs (an admin's
   * channel edit or settings write, maintenance included, to everyone; a ban/unban, to that
   * player). Unlike {@link loadProfile} it never flips `status` to «loading», so nothing is swapped
   * for the boot splash: a live table is never UNMOUNTED (its cleanup is a CONSENTED leave — the
   * seat to a bot at once, and a public حکم table with both partners hit ENDED with a fabricated
   * rated win: staging 2026-10-04, runs 26–31), and the presence socket that carries the next push
   * stays up. A refusal met in the GAME view waits — see {@link sessionRecheckPending}.
   */
  refreshSession: () => Promise<void>;
  /**
   * A background re-read was REFUSED (maintenance, the channel gate, a ban) while a game view was on
   * screen. No admin action may throw a player off a live table or end their match (operator,
   * 2026-10-04), so the match is finished first: the moment the player leaves the game view the
   * standing is asked AGAIN — the server's answer then, not the stale one — and its screen shows.
   * Cleared by any later successful re-read (maintenance off, an unban).
   */
  sessionRecheckPending: boolean;
  saveProfile: (input: UpdateProfileInput) => Promise<void>;

  // ── Profile actions ──
  /** Live availability probe for an edited app-username (debounced by the screen). */
  checkUsername: (username: string) => Promise<UsernameAvailability>;
  /** Upload a prepared image (see `prepareAvatarUpload`); `profile.avatarPhotoId`
   * updates only after the server confirms, so the new photo shows exactly when it
   * is really stored. Returns the upload metadata (or throws on failure). */
  uploadAvatar: (data: Blob, mime: string) => Promise<AvatarUploadResult>;
  /** Remove the uploaded photo; the profile falls back to the preset avatar.
   * Local state clears only after the server confirms the delete. */
  removeAvatarPhoto: () => Promise<void>;
  /** Toggle the notifications preference (persists via `PATCH /me/notifications`). */
  updateNotifications: (enabled: boolean) => Promise<void>;
  /** Refresh stats from `GET /me/stats` (already seeded from the profile on load). */
  loadStats: () => Promise<void>;
  /** Load the first page of match history (resets any loaded rows). */
  loadHistory: () => Promise<void>;
  /** Append the next page of match history (no-op when exhausted or already loading). */
  loadMoreHistory: () => Promise<void>;
  /** Fetch the resumable in-progress games for the Home strip. */
  loadActiveGames: () => Promise<void>;
  /** Drop one game from the local resume strip immediately — called after the
   * server ACKed a «پایان بازی» (its ActiveGame row is provably deleted; the next
   * lobby fetch just confirms). Never call it on an unconfirmed end. */
  clearActiveGame: (game: string) => void;

  // ── Friends actions ──
  /** The single refresh seam: reloads friends + incoming + outgoing in parallel.
   * Called on Friends-tab focus and the ~20s poll; swap for the realtime push
   * subscription when the presence/notification layer lands. */
  refreshFriends: () => Promise<void>;
  /** Send a friend request; the returned view lands in `outgoing`. */
  requestFriend: (userId: string) => Promise<FriendView>;
  /** Accept an incoming request; the user moves from `incoming` to `friends`. */
  acceptFriend: (userId: string) => Promise<FriendView>;
  /** Reject an incoming request; the user leaves `incoming`. */
  rejectFriend: (userId: string) => Promise<FriendView>;
  /** Cancel an outgoing request; the user leaves `outgoing`. */
  cancelFriend: (userId: string) => Promise<FriendView>;
  /** Remove an accepted friend; the user leaves `friends`. */
  removeFriend: (userId: string) => Promise<FriendView>;
  /** Block a user (from the DM thread). Server-side this replaces ANY existing
   * relationship (friendship/request) with a block and removes the pair's
   * follows, so the user leaves every local list too. */
  blockUser: (userId: string) => Promise<FriendView>;
  /** Unblock a previously-blocked user (surfaced from search / the DM thread). */
  unblockUser: (userId: string) => Promise<FriendView>;
  /** Load «کاربران مسدود» (`GET /friends/blocked`). Safe to call on every screen open. */
  loadBlocked: () => Promise<void>;
  /**
   * Read the «برترین‌ها» board for one (period, game).
   *
   * ⚠️ CALLED ON OPEN AND ON RETURNING TO THE TAB — never on a timer. `quiet` keeps whatever is
   * already on screen while it refreshes, so coming back to the tab never flashes a spinner.
   * A superseded request never paints: the store keeps only the newest request's result.
   */
  loadLeaderboard: (
    period: LeaderboardPeriod,
    game: GameId | "all",
    opts?: { quiet?: boolean },
  ) => Promise<void>;
  /**
   * Remember where «برترین‌ها» is standing, then open that player's profile.
   *
   * One call, not two, because the two halves must not be separable: saving without navigating
   * leaves a stale offset that the next visit would restore for no reason, and navigating without
   * saving is the bug this exists to fix.
   */
  openProfileFromLeaderboard: (
    userId: string,
    at: { period: LeaderboardPeriod; game: GameId | "all"; scrollTop: number },
  ) => void;
  /** Read back that position ONCE, clearing it. Null on any other route onto the screen. */
  takeLeaderboardReturn: () => {
    period: LeaderboardPeriod;
    game: GameId | "all";
    scrollTop: number;
  } | null;

  // ── DM-unread actions ──
  /** Reload the persisted per-thread unread counts (`GET /me/dm-unread`) —
   * on session ready + the ~20s poll (the fallback when a push is missed). */
  refreshDmUnread: () => Promise<void>;
  /** One-sided «حذف گفتگو» from the chat list: hide the peer's thread from MY
   * list (server + local preview/unread). The peer keeps the conversation. */
  hideDmThread: (peerId: string) => Promise<void>;

  // ── Inbox actions ──
  /** Load the inbox list + unread (the Messages screen); also marks all read server-side. */
  loadInbox: () => Promise<void>;
  /** Refresh JUST the unread count (`GET /me/inbox`) — boot + the ~20s poll. */
  refreshInboxUnread: () => Promise<void>;
  /** Delete ONE inbox message (optimistic local removal, then `DELETE /me/inbox/:id`). */
  deleteInboxMessage: (id: string) => Promise<void>;
  /** Clear the whole inbox (optimistic local clear, then `DELETE /me/inbox`). */
  clearInbox: () => Promise<void>;

  // ── Game-invite actions ──
  /** Reload incoming + outgoing live invites (best-effort; pushes keep them live). */
  refreshInvites: () => Promise<void>;
  /** Invite a friend to the table `roomId` of `game` (`POST /invites`). */
  sendGameInvite: (
    toUserId: string,
    game: GameId,
    roomId: string,
    opts?: {
      /** MID-GAME seat-fill (feature 3): the target bot chair + the reconnection token minted
       *  for this invitee. Omitted → an ordinary pre-start invite. */
      seat?: number;
      reconnectToken?: string;
    },
  ) => Promise<GameInviteView>;
  /** Accept an incoming invite, then enter its table via the existing
   * join-by-roomId path (`joinPrivate`, the `t_<game>_<roomId>` route). On a 409
   * (expired / no longer pending) the local invite is dropped and the error is
   * rethrown so the prompt can show the Persian reason. */
  acceptGameInvite: (inviteId: string) => Promise<AcceptGameInviteResponse>;
  /** Decline an incoming invite. An already-expired/decided invite counts as
   * done (the row is dropped either way). */
  declineGameInvite: (inviteId: string) => Promise<void>;
  /** Withdraw an invite I sent; the row flips to CANCELED. */
  cancelGameInvite: (inviteId: string) => Promise<GameInviteView>;
  /** The friend-row «دعوت به بازی» flow: host-create a private table for `game` with
   * the default rules and remember the friend FOR THAT CREATE — the invite panel of the
   * table it produces auto-sends the invite as soon as the roomId exists, and no other
   * table's panel ever does ({@link SessionState.pendingInvite}). */
  inviteFriendToGame: (toUserId: string, game: GameId) => void;
  /** Consume the pending friend-to-invite marker (waiting-room panel). */
  clearPendingInvite: () => void;
  /** Open the incoming-invite prompt for a known invite id (toast tap). */
  openInvitePrompt: (inviteId: string) => void;
  /** Close the incoming-invite prompt overlay. */
  dismissInvitePrompt: () => void;
  /** Raise the "switching game" transition overlay with `message` (accepting an invite while in a
   *  game). Auto-clears after a safety timeout if the invited game never connects. */
  beginGameSwitch: (message: string) => void;
  /** Drop the switch overlay (the invited game finished connecting, or the switch failed). Honors
   *  a short minimum-visible hold so it never flashes on an instant connect. */
  clearGameSwitch: () => void;
  /** Show the newest live incoming invite as the prompt (missed-while-offline
   * sweep on app open); no-op while a prompt is already up. */
  promptNextIncomingInvite: () => void;
  /**
   * THE SEAT-FILL TOKEN THE SILENT ACCEPT CAME BACK WITH (card-c1) — for a table being walked into
   * through its Telegram button, whose `joinById` a dealt table refuses. In memory only; the table
   * screen's connector spends it at its ticket door ({@link takeSeatFillTicket}).
   */
  seatFillTicket: { readonly roomId: string; readonly token: string } | null;
  /** Hand over {@link seatFillTicket} if it opens `roomId`, once: a second ask gets null. */
  takeSeatFillTicket: (roomId: string) => string | null;
  /**
   * THE TABLE WHOSE SEAT-FILL KEY MAY STILL BE ON ITS WAY (card-c1): the sweep's list is out for the
   * table being walked into, or a seat row's silent accept is. Its lock refusal waits behind the
   * connecting screen. Falls when the last of them answers, or at {@link SEAT_FILL_ACCEPT_HOLD_MS}. A
   * row with no seat raises nothing: it can bring no key.
   */
  seatFillAccepting: string | null;
  /**
   * THE SWEEP ON APP OPEN: fetch the invite list and settle it ({@link promptNextIncomingInvite}),
   * with {@link seatFillAccepting} up meanwhile: a seat-fill row may be in it.
   */
  sweepInvites: () => void;

  // ── Games actions ──
  /** Fetch/refresh the `GET /games` activation list (once on session ready +
   * on every Lobby mount — cheap, the server caches ~5s). Deduped while one is
   * in flight; a failure keeps the last-known list (or the static fallback) —
   * the game-server still enforces the real gate on join. */
  loadGames: () => Promise<void>;

  // ── Presence actions ──
  /** Open the background presence socket (idempotent). Live pushes drive friends'
   * presence + fire `refreshFriends` on a request/accept. No-op without a
   * `presenceConnector` in deps; the ~20s poll remains the fallback either way. */
  connectPresence: () => void;
  /** Tear down the presence socket and clear live presence. */
  disconnectPresence: () => void;
  /** Tell the server whether the user is currently in a game room (in-game
   * presence). `game` names WHICH game while in-game — it feeds the live
   * per-game lobby counters («مار» too); pass null when leaving (or when the active game
   * id is unknown — the user still counts as in-game for the friend dots). */
  setInGame: (inGame: boolean, game?: PresenceGameId | null) => void;
  /** Dismiss the friend-event toast. */
  dismissFriendToast: () => void;
}

export interface SessionDeps {
  api: ApiClient;
  initDataRaw?: string | null;
  /** Background presence/friend-realtime socket; injected so it is fakeable in tests. */
  presenceConnector?: PresenceConnector;
}

/** The Persian game title for an invite payload's game id («حکم», «بی‌دل», …). */
function gameTitleFa(gameId: string | undefined): string {
  return GAMES.find((g) => g.id === gameId)?.fa ?? "";
}

/** The Persian toast line for a live social push — one natural sentence per kind
 * (friend kinds mirror the bot notification; invite kinds name the game). Null
 * kinds show NO toast: `new_follower` because the follow feature is UI-removed
 * (a server may still publish it — ignore gracefully), and any kind a newer
 * server ships before this client knows it. */
function friendEventToast(push: FriendEventPush): string | null {
  const name = push.actor.name;
  switch (push.type) {
    case "friend_request":
      return `${name} ${fa.friends.pushRequest}`;
    case "friend_accept":
      return `🎉 کاربر ${name} ${fa.friends.pushAccept}`;
    case "new_follower":
      return null;
    case "dm_message":
      return `${name} ${fa.chat.pushDm}`;
    case "game_invite":
      return fa.invite.pushInvite(name, gameTitleFa(push.invite?.game));
    case "invite_accepted":
      // NO TOAST — and therefore no chime. You are sitting at the table you invited them
      // to: they WALK INTO your waiting room, the roster shows them and sounds their
      // arrival. A toast here announced the same event a second time, and chimed on top
      // of the arrival cue — the reported DOUBLE SOUND. (The outgoing invite chip still
      // flips to «پیوست»; that is handled by the push, not by this toast.)
      return null;
    case "invite_declined":
      return fa.invite.pushDeclined(name);
    case "invite_canceled":
      return fa.invite.pushCanceled(name);
    case "invite_expired":
      return fa.invite.pushExpired(name);
    case "game_replaced":
      // You left an online seat (still reclaimable) and the owner filled it with a friend —
      // the game is over for you. Named so you know who took your chair.
      return fa.online.replacedNotice(name);
    default:
      return null;
  }
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error";
}

/**
 * The message for the boot ERROR screen. Four tiers, in order.
 *
 * ① A server-supplied PERSIAN reason (a gate, a ban) is meaningful and is kept verbatim.
 *
 * ⓪ …then, INSIDE the coded tier, the one verdict the api cannot disambiguate: a 401
 *    `AUTH_INVALID` reaching a client that Telegram DID launch (`launchParamsUnusable`) is not
 *    «you are outside Telegram», it is «this device could not sign in». Measured 2026-09-06.
 *
 * ② A server error with a stable `code` is localized through `fa.errors` — the table that already
 *    exists for exactly this. ⚠️ THIS TIER WAS MISSING, AND IT MADE THE APP LIE. `client.ts`
 *    throws the server's RAW message, which for the auth failures is English («Missing Telegram
 *    initData», «Bad signature»), and a 4xx is deliberately NOT retried
 *    (`isTransientBootError`) — so it fell straight through to ③ and told a player whose initData
 *    was invalid that their internet was slow, while `fa.errors.AUTH_INVALID` («خطای ورود؛ برنامه
 *    را از تلگرام باز کن») sat unused. `fa.errors` is `satisfies Record<ApiErrorCode, string>`, so
 *    a narrowed code always has a string.
 *
 * ③ Anything else — a fetch rejection, our own abort timeout, a bug — is a genuine «no answer
 *    yet», and only THAT gets the offline line. ⚠️ Deliberately NOT `faApiError`: its unknown-code
 *    fallback is the friends-action retry line, which is wrong on a boot screen.
 */
export function bootErrorMessage(
  error: unknown,
  launch: Pick<LaunchContext, "launchParamsUnusable"> = resolveLaunchContext(),
): string {
  const msg = messageOf(error);
  if (/[؀-ۿ]/.test(msg)) return msg;
  if (error instanceof ApiError && error.code) {
    // ⓪ THE ONE VERDICT THE SERVER CANNOT TELL APART, AND THE CLIENT CAN. A 401 `AUTH_INVALID`
    // means «no usable initData reached us» — which is BOTH «opened in a plain browser» and
    // «Telegram launched us onto an engine that could not read the launch». Only the second one
    // has a person in Telegram reading «برنامه را از تلگرام باز کن» and having nowhere to go.
    // `launchParamsUnusable` is the only signal that separates them (see `telegram/launch.ts`).
    if (error.code === "AUTH_INVALID" && launch.launchParamsUnusable) {
      return fa.boot.signInFailedOnDevice;
    }
    return fa.errors[error.code];
  }
  return fa.status.offline;
}

/** What the boot error screen should offer beside the message — see {@link bootErrorAction}. */
export type BootErrorAction = "retry" | "reload" | null;

/**
 * …AND WHICH ACTION BELONGS BESIDE IT. Kept next to {@link bootErrorMessage} because the two walk
 * the same failure and MUST agree — the pair is pinned by a test that ranges over every status ×
 * code («never shows the offline line, which promises a retry, without the retry»).
 *
 * ⚠️ THE THREE ANSWERS ARE NOT TWO. «retry» re-runs `loadProfile`, i.e. re-asks the SAME question
 * with the SAME initData; «reload» throws the page away, which is the ONLY thing that can mint
 * fresh Telegram initData. A stale session needs the second and is untouched by the first, so a
 * boolean here would have to pick one of «lie» or «dead end».
 *
 * - **`AUTH_EXPIRED` ⇒ `reload`.** `client.ts` heals this itself with ONE guarded reload — but the
 *   guard lives in `sessionStorage`, which SURVIVES that reload, so a second expiry (or a
 *   storage-less webview, where it never armed) reopens nothing. The screen must therefore carry
 *   the reload itself rather than promise one.
 * - **Any other CODED error ⇒ `isTransientBootError`**, the very predicate `resilientRequest` uses
 *   to decide whether to retry BY ITSELF: a 4xx is the same answer however often it is asked (the
 *   app was opened outside Telegram, the account is banned) ⇒ `null`; a 5xx is our own hiccup and
 *   passes ⇒ `retry`.
 * - **Anything UNCLASSIFIED ⇒ `retry`.** `client.ts` reads the code out of the error BODY, so an
 *   answer from the EDGE rather than from us has none — a Cloudflare rate-limit or WAF page, an
 *   nginx 404 on a misrouted `/api/` — and the message falls to tier ③, `fa.status.offline`, which
 *   ENDS «دوباره تلاش کنید». Calling that 4xx permanent asks for a retry it does not offer. It is
 *   also not OUR verdict to make permanent: nothing about a 429 says the next attempt fails.
 *
 * ⚠️ Do NOT "simplify" the middle case by widening `isTransientBootError` itself — that predicate
 * is shared with the AVATAR UPLOAD, whose policy deliberately does not retry a codeless 413.
 *
 * ⚠️ Keyed on the HTTP VERDICT, never on which TIER wrote the text: a Persian server reason is
 * MAINTENANCE (503, passes with time) or BANNED (403, never).
 */
export function bootErrorAction(error: unknown): BootErrorAction {
  if (!(error instanceof ApiError) || !error.code) return "retry";
  if (error.code === "AUTH_EXPIRED") return "reload";
  return isTransientBootError(error) ? "retry" : null;
}

/**
 * Whether a BACKGROUND `/me` refusal (a `session` push's re-read, outside a game) must take the
 * screen: a coded, permanent verdict with no screen of its own — a ban, a dead or invalid session.
 * MAINTENANCE and CHANNELS_REQUIRED draw their own screens off the flags the api client sets, and a
 * transient or uncoded failure says nothing about the player at all.
 */
export function takesTheScreen(error: unknown): boolean {
  if (!(error instanceof ApiError) || !error.code) return false;
  if (error.code === "CHANNELS_REQUIRED") return false;
  // MAINTENANCE is a 503, i.e. transient, so it falls out here too.
  return !isTransientBootError(error);
}

/** Match-history page size (rows fetched per `loadHistory` / `loadMoreHistory`). */
const HISTORY_PAGE = 20;

/**
 * How often a session that BOOTED INTO MAINTENANCE asks whether it is over — see the poll at the
 * foot of {@link createSessionStore}. Such a client has no presence socket (presence connects only
 * on «ready»), so the OFF push cannot reach it; this is the only way back short of reopening the app.
 */
export const MAINTENANCE_OFF_POLL_MS = 12_000;

/** Game-switch transition overlay: minimum time it stays up (no flash on an instant connect) and
 *  the safety ceiling after which it force-drops even if the invited game never connects. */
const GAME_SWITCH_MIN_MS = 1100;
const GAME_SWITCH_MAX_MS = 15_000;

/**
 * THE MOST THE INVITE SWEEP MAY HOLD A LOCK REFUSAL BEHIND THE CONNECTING SCREEN (card-c1), from the
 * sweep's start: the list and a seat row's accept share it. The join starts about 2 s after the sweep
 * on a plain boot, so at most ~4 s on screen there, 6 s on a slow boot. Past it: today's refusal, and a
 * token that still lands re-runs the join as it always did. It is also the worst an ordinary lock
 * refusal can wait, on a slow boot with the list hung; normally that wait is what is left of the list.
 */
export const SEAT_FILL_ACCEPT_HOLD_MS = 6_000;

/**
 * Has this session's api been seen to lack `GET /friends/all`? Module-level, so ONE 404 costs one
 * extra round-trip and every later poll goes straight to the three singles instead of re-probing
 * a route that is not there.
 */
let friendListsUnsupported = false;

/**
 * The three friend lists, in ONE request where the server supports it.
 *
 * `/friends/all` replaces three authenticated polls with one — MEASURED on prod, those three
 * were 54.3% of every api request. But the web and the api deploy INDEPENDENTLY: a bundle that
 * lands before the api (or outlives an api rollback) would poll a route that answers 404, and a
 * friends list that silently empties is much worse than one extra request. So a NOT_FOUND falls
 * back to the three singles — permanently for this session — and any other failure propagates
 * as it always did (a real error must still surface as `friendsError`).
 */
async function fetchFriendLists(api: SessionDeps["api"]): Promise<FriendLists | Unchanged> {
  if (!friendListsUnsupported) {
    try {
      // Propagated, never swallowed: a 304 here means «your three lists are still right».
      return await api.listFriendLists();
    } catch (error) {
      if (!isNotFound(error)) throw error;
      friendListsUnsupported = true;
    }
  }
  // The version-skew fallback (an api with no `/friends/all`) is unconditional by nature — the
  // three singles carry no ETag — so it always returns a body, which is the safe direction.
  const [friends, incoming, outgoing] = await Promise.all([
    api.listFriends(),
    api.listIncoming(),
    api.listOutgoing(),
  ]);
  return { friends, incoming, outgoing };
}

/**
 * Collapse overlapping refreshes of ONE slice: at most one request in flight, and at most one
 * queued behind it.
 *
 * ⚠️ WHY IT IS NEEDED NOW, AND WHY THE QUEUED RUN IS NOT OPTIONAL. Three of the nine friend
 * mutations carry BOTH a legacy `friend_request`/`friend_accept` push AND the new
 * `data-changed { friends }`, and both correctly end in «re-read your friends list» — so one
 * friend request measured on staging cost **five** `/friends/all` calls across three clients
 * instead of three, and the badge landed ~23 ms later because React committed two responses
 * instead of one. Removing one of the two pushes is NOT the fix: gating the data-event on the
 * notifier event is precisely the bug that made six of nine mutations silent for months.
 *
 * The TRAILING run is what keeps this safe. Simply returning the in-flight promise would serve a
 * push that landed AFTER the request was issued with data read BEFORE it — a badge that stays
 * wrong until the safety net. So a call during a flight sets a flag, and the flight re-runs once
 * when it lands: N pushes cost at most 2 requests, and the last one is always honoured.
 */
function coalesced(run: () => Promise<void>): () => Promise<void> {
  let inflight: Promise<void> | null = null;
  let queued = false;
  const start = (): Promise<void> => {
    const p = run().finally(() => {
      inflight = null;
      if (queued) {
        queued = false;
        void start();
      }
    });
    inflight = p;
    return p;
  };
  return () => {
    if (!inflight) return start();
    queued = true;
    return inflight;
  };
}

/** Is this the api telling us the route does not exist (as opposed to any other failure)? */
function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.code === "NOT_FOUND");
}

/** TEST-ONLY: forget the «api has no /friends/all» latch between cases. */
export function __resetFriendListsSupportForTests(): void {
  friendListsUnsupported = false;
}

/** Drop `userId` from a list of views. */
function without(list: readonly FriendView[], userId: string): FriendView[] {
  return list.filter((f) => f.userId !== userId);
}

/** Replace (or append) `view` in a list, keyed on `userId`. */
function upsert(list: readonly FriendView[], view: FriendView): FriendView[] {
  return [...without(list, view.userId), view];
}

/** Replace (or prepend — the lists are newest-first) `invite`, keyed on `id`. */
function upsertInvite(list: readonly GameInviteView[], invite: GameInviteView): GameInviteView[] {
  return [invite, ...list.filter((i) => i.id !== invite.id)];
}

// `isLiveInvite` now lives in `game/invite-clock.ts` — it used to compare the SERVER's `expiresAt`
// against this DEVICE's `Date.now()`, which meant a phone running fast could never answer an
// invite (it saw them all as already expired) while still being TOLD about each one by the toast.
// See that module's header for the measurement and the fix.

/**
 * The online table this session is IN or walking into, or null when it is in no game at all.
 *
 * An incoming invite naming THIS room is one the user has already acted on — or one they beat
 * by arriving another way — so it must never be prompted again (see `promptNextIncomingInvite`):
 * «yes» would run `joinPrivate` and tear down the live connection with a second join.
 *
 * ⚠️ THE LIVE ROOM COMES FIRST, AND THE INTENT IS ONLY A FALLBACK. `onlineJoin` carries a roomId
 * for `kind:"join"` only, so a QUICK-MATCHER — whose room the matchmaker picks — used to answer
 * null here and get prompted for the very table they were sitting at. That was unreachable while
 * only private tables could be invited to; public-table invites make it reachable, because a
 * friend can be invited to a public table AND be matchmade into that same table. `liveRoomId` is
 * written from the connector's `onLive`, so it is true for every join kind.
 *
 * The intent fallback is kept for the window BEFORE the socket connects: an accepted invite sets
 * `kind:"join"` immediately, and the sweep can run while the table is still «connecting».
 */
function joinedRoomId(
  state: Pick<SessionState, "online" | "onlineJoin" | "view" | "liveRoomId">,
): string | null {
  if (!state.online || state.view !== "game") return null;
  if (state.liveRoomId) return state.liveRoomId;
  const join = state.onlineJoin;
  return join.kind === "join" ? join.roomId : null;
}

/**
 * THE FRIENDS-SCREEN PICK TO AUTO-SEND AT THE TABLE ON SCREEN — the friend's id while the online
 * container runs the very create intent `inviteFriendToGame` made for them, else null.
 *
 * Hosting a table is not having created it for the pick (card-a5 item 15): a guest crowned before
 * the start, an owner who follows their «بازی دوباره» into the successor pinned to them, and a
 * table created later with the same default rules all reach a HOST's invite panel, and none of
 * them is the table the pick asked for. Identity, not equality — see `SessionState.pendingInvite`.
 */
export function pendingInviteHere(
  state: Pick<SessionState, "pendingInvite" | "onlineJoin">,
): string | null {
  const pick = state.pendingInvite;
  return pick !== null && pick.join === state.onlineJoin ? pick.toUserId : null;
}

/** Sum a per-peer unread map (the چت tab badge value). */
function sumUnread(unread: Record<string, number>): number {
  return Object.values(unread).reduce((total, n) => total + n, 0);
}

/**
 * The lobby list rendered while `GET /games` is in flight: the canonical
 * `LOBBY_GAME_ORDER` at the SHARED `DEFAULT_GAME_STATUSES` — the same map the api's
 * boot-seed writes into `GameConfig` and the game-server's gate falls back to. Once the
 * response lands, the server truth replaces this unconditionally.
 *
 * It used to read a hand-maintained `enabled` boolean on the web's own catalogue, which had
 * drifted from both: شلم/پاسور/بلوف rendered PLAYABLE while the real status was COMING_SOON
 * (a tap in that window was refused by the server), and منچ flashed «بزودی» although it is
 * live. One source means the three can no longer disagree.
 *
 * A game hidden from players (`HIDDEN_GAMES` — بلوف, operator 2026-10-03) is dropped here AND from
 * the server's list in `loadGames`, so the grid, the invite sheet and «برترین‌ها»'s chips never see
 * it; the survivors keep their canonical `order`, the same index the server sends.
 */
export const FALLBACK_LOBBY_GAMES: GameActivation[] = LOBBY_GAME_ORDER.map((game, order) => ({
  game,
  status: DEFAULT_GAME_STATUSES[game],
  order,
})).filter((entry) => !isHiddenGame(entry.game));

/** Every game at zero — the counts before the presence socket's first push.
 * Spelled out so adding a game to the domain is a compile error here (keep it exhaustive).
 * «مار»'s `snake` is left out ON PURPOSE: only a game-server that counts it may put it on the tile,
 * so an older one shows no count there rather than a false ۰. */
export function zeroGameCounts(): GameCounts {
  return {
    hokm: 0,
    hearts: 0,
    shelem: 0,
    pasur: 0,
    haftkhabis: 0,
    bluff: 0,
    chess: 0,
    backgammon: 0,
    mensch: 0,
    ladders: 0,
    dooz: 0,
  };
}

/**
 * The session store as a framework-free vanilla Zustand store, so it is fully
 * unit-testable without React. `context.tsx` binds it into the component tree.
 */
export function createSessionStore(deps: SessionDeps) {
  // The presence socket is imperative + long-lived (one per session), so it lives
  // in a closure rather than store state. `gen` invalidates an in-flight connect
  // that a teardown (e.g. React StrictMode's mount/unmount/mount) has superseded.
  let presenceHandle: PresenceHandle | null = null;
  let presenceConnecting = false;
  let presenceGen = 0;
  /**
   * THE FIRST JOIN'S RETRY — `manageRoom` is handed an already-joined room, so the reconnect cycle
   * covers only drops AFTER it. An app opened while the game-server restarts (a 503, a 502, a join
   * the draining server refuses) used to have NO presence for the whole session. A refused first
   * join is retried on the cycle's own backoff, for the cycle's own budget and attempt cap — as long
   * as a dropped presence socket would be — and the schedule restarts only after a teardown.
   */
  let presenceRetry: ReturnType<typeof setTimeout> | undefined;
  let presenceAttempts = 0;
  let presenceFirstAttemptAt = 0;
  // A boot `loadProfile` runs its own retry loop, so a second overlapping call (the boot sequence's
  // 8s stall-retry, or a fast double-tap of «تلاش مجدد») must not start a SECOND loop racing the
  // first. It clears the moment the loop settles (ready or error), so the manual retry button — only
  // reachable AFTER the error screen shows — always works.
  let profileInFlight = false;
  // A background `refreshSession` in flight, and whether another push asked again meanwhile — the
  // second ask runs once the first settles, so a quick «maintenance on → off» is answered with the
  // LATER state, never dropped behind the earlier one.
  let sessionInFlight = false;
  let sessionAskedAgain = false;
  // Desired in-game state (+ which game), remembered so it is applied even if
  // `setInGame` was called before the socket finished connecting (e.g.
  // deep-linked into a game).
  let wantActivity: { inGame: boolean; game: PresenceGameId | null } = {
    inGame: false,
    game: null,
  };
  /**
   * FOREGROUND — the app is actually in front of the user.
   *
   * Mirrors `wantActivity`: remembered here so a flip that happens before the socket lands is
   * still applied, and reported to the presence room so the server-side «in the app» marker
   * tracks ATTENTION rather than the socket. A backgrounded WebView keeps its socket alive, so
   * without this an invite arriving while the phone was locked was pushed to a screen nobody
   * was watching AND had its Telegram PV suppressed — it reached the user through neither
   * channel and expired unseen.
   */
  let wantForeground = true;
  /** Unsubscribe from the lifecycle's fused activity signal (set while presence is connected). */
  let offForeground: (() => void) | null = null;
  // Dedupe guard for `loadGames` (session-ready + Lobby mount can race at boot).
  let gamesLoading = false;
  /** See `loadLeaderboard` — guards against an out-of-order response painting. */
  let leaderboardEpoch = 0;
  // Game-switch overlay timers (accept-invite-while-in-a-game): when it was raised (for the
  // min-visible hold) + the safety timeout id (force-clear if the invited game never connects).
  let gameSwitchAt = 0;
  let gameSwitchMaxTimer: ReturnType<typeof setTimeout> | null = null;
  // `seatFillAccepting`'s two guards: the requests still out (the list, a seat row's accept), and the
  // bound under them.
  let seatFillRequestsOut = 0;
  let seatFillHoldTimer: ReturnType<typeof setTimeout> | null = null;
  // The maintenance-OFF poll: the wait it serves (a latch — see the foot of this function), one ask
  // (assigned inside the store, where `get`/`set` live), its interval, and its foreground hook.
  let offWait = false;
  let probeMaintenanceOff: () => Promise<void> = () => Promise.resolve();
  let offPollTimer: ReturnType<typeof setInterval> | null = null;
  let offPollForeground: (() => void) | null = null;

  const store = createStore<SessionState>((set, get) => {
    // Poll↔mutation race guards. A background poll (or a push-triggered refresh) issues its GETs,
    // then a local mutation/push updates the store; when the poll's OLDER server snapshot resolves
    // it must NOT clobber the newer local truth. Each mutation bumps an epoch; a refresh captures
    // the epoch before its fetch and skips its wholesale `set` if the epoch moved meanwhile (the
    // next poll reconciles). Closure counters (not store state) → no extra re-renders.
    let friendsEpoch = 0;
    let dmUnreadEpoch = 0;
    let invitesEpoch = 0;

    /**
     * THE PUBLIC TABLE THIS DEVICE HOLDS A SEAT AT, IN PROGRESS — or null (card-a5 item 1). Only a
     * dealt public table: a waiting room is not a game anybody can be charged for leaving.
     */
    const heldPublicTable = (): ReturnType<typeof straightBackTarget> => {
      const held = straightBackTarget();
      return held !== null && !held.waiting ? held : null;
    };

    /**
     * AN ACT ELSEWHERE WHILE A PUBLIC SEAT IS HELD — ROUTED THROUGH THAT TABLE FIRST (card-a5 item 1).
     *
     * The operator: «being active in the app during the hold without returning counts exactly like
     * «خروج»». Starting or joining another game (private or solo) while this device holds a seat at a
     * public table in progress is that act. It is not refused and not charged here: the app goes back
     * to the table (a `rejoin`, the same door «ادامه بازی» opens) carrying the act, and the table puts
     * «خروج»'s own confirmation in front of it — confirm leaves exactly as «خروج» leaves (the same
     * record, the same cost, the same path: the `leave-forever` frame on a live socket) and then does
     * the act; cancel stays at the table; a seat that turns out to be gone just does the act.
     *
     * Only from OUTSIDE the game view — at a table, `setView`'s guard owns the question. Never for the
     * held table itself (going back to it IS returning). Returns whether it routed the act.
     */
    const viaHeldTable = (intent: ExitIntent, targetRoomId?: string): boolean => {
      if (get().view === "game") return false;
      const held = heldPublicTable();
      if (held === null) return false;
      if (targetRoomId !== undefined && targetRoomId === held.roomId) return false;
      set({
        activeGame: held.game,
        online: true,
        onlineJoin: { kind: "rejoin", roomId: held.roomId, token: held.token, then: intent },
        view: "game",
      });
      return true;
    };

    /** Zero one peer's unread count (their thread just opened — the DM room
     * persists the same clear via `markRead` on join). */
    const clearDmUnread = (peerId: string): void => {
      const s = get();
      if (!s.dmUnread[peerId]) return;
      const dmUnread = { ...s.dmUnread };
      delete dmUnread[peerId];
      dmUnreadEpoch += 1;
      set({ dmUnread, dmUnreadTotal: sumUnread(dmUnread) });
    };

    /**
     * What a session does the moment it turns «ready» — shared by {@link SessionState.loadProfile}
     * and the maintenance-OFF poll below, so a boot that met maintenance and recovered without a
     * reload loses none of it (an `f_` follow or a `c_` thread its deep link was holding).
     */
    const afterProfileReady = (): void => {
      // The once-on-session-ready activation fetch (the Lobby refreshes on
      // mount too; `loadGames` dedupes the boot race). Fire-and-forget —
      // it never throws and the fallback list keeps the lobby rendering.
      void get().loadGames();
      // An `f_<telegramId>` FOLLOW deep link waited for this authed moment:
      // send the inviter ONE friend request (best-effort, once per inviter).
      const followId = get().pendingFollowId;
      if (followId) {
        set({ pendingFollowId: null });
        void (async () => {
          const guardKey = `bing:followed:${followId}`;
          try {
            if (sessionStorage.getItem(guardKey)) return;
          } catch {
            /* storage unavailable → still best-effort below */
          }
          try {
            // ONE server-side resolver for every ref shape (opaque token /
            // legacy ids) — the client never needs to know what the ref is.
            await deps.api.followByRef(followId);
            try {
              sessionStorage.setItem(guardKey, "1");
            } catch {
              /* ignore */
            }
            void get().refreshFriends();
          } catch {
            /* self / already-friends / blocked / unknown — silent on boot */
          }
        })();
      }
      // A `c_<appUserId>` deep link — the button on the "you have a new message"
      // Telegram PV. Open that conversation once we know who the peer is (the thread
      // renders from their FriendView, so the friend list has to be in first).
      const chatPeerId = get().pendingChatPeerId;
      if (chatPeerId) {
        set({ pendingChatPeerId: null });
        void (async () => {
          try {
            await get().refreshFriends();
            const peer = get().friends.find((f) => f.userId === chatPeerId);
            // ⚠️ Never OVER a table: the maintenance-OFF poll can land this while the player sits at
            // one they returned to, and leaving the game view unmounts it (a consented leave).
            if (peer && get().view !== "game") get().openChat(peer); // not a friend any more → just land in the app
          } catch {
            /* best-effort: the app still opens, on the lobby */
          }
        })();
      }
    };

    /**
     * ONE ASK OF THE MAINTENANCE-OFF POLL — see the subscription at the foot of
     * {@link createSessionStore}. ONE attempt (the interval IS the retry; the boot's own backoff
     * would overlap it) under the boot's per-attempt bound, so a request the link swallows is cut
     * off rather than holding the poll for ever. And it never writes «loading»: that is the boot
     * splash, which would unmount a table the player went back to from the maintenance screen.
     */
    let offProbeInFlight = false;
    probeMaintenanceOff = async () => {
      // Only the poll calls this — its interval, its foreground hook, and the flag falling while it
      // waits — and all three are gone in the same store write that ends the wait, so «is it still
      // wanted?» is theirs to answer, not this. A boot load in flight is already asking, and so is
      // an earlier probe.
      if (offProbeInFlight || profileInFlight) return;
      offProbeInFlight = true;
      try {
        // A success is maintenance OFF: the api client has already cleared the flag.
        const profile = await resilientGetMe(deps.api.getMe, { attempts: 1 });
        set({ profile, stats: profile.stats, status: "ready", error: null });
        afterProfileReady();
        // The boot sequence's own first friends / DM reads met the 503; ask them now, not at the
        // next badge tick.
        void get().refreshFriends();
        void get().refreshDmUnread();
      } catch (error) {
        // Still maintenance (a 503), or no answer at all: ask again on the next tick. ⚠️ EXCEPT the
        // channel check's own 503 (card-a5 item 5): the api answers MAINTENANCE before it ever reaches
        // the channel gate, so «Telegram did not answer» PROVES maintenance is over — keeping the
        // player on the «به‌روزرسانی» screen would be false, with no retry. It is the verdict below.
        const checkUnavailable =
          error instanceof ApiError && error.code === "CHANNEL_CHECK_UNAVAILABLE";
        if (isTransientBootError(error) && !checkUnavailable) return;
        // A different VERDICT — a ban, the channel gate, a dead session. Nothing else ends this
        // wait but «ready», which this answer will never give: end it here (the latch first, so the
        // flag's own fall below reads as the end and not as news), and let the verdict speak. AT A
        // TABLE IT WAITS (as `refreshSession`'s does): the screen would replace the table, so the
        // standing is asked again the moment the player leaves it.
        offWait = false;
        get().setMaintenance({ active: false, message: "" });
        if (get().view === "game") set({ sessionRecheckPending: true });
        else set({ error: bootErrorMessage(error), errorAction: bootErrorAction(error) });
      } finally {
        offProbeInFlight = false;
      }
    };

    /** Drop an incoming invite locally (accepted/declined/canceled/expired),
     * closing the prompt if it is showing exactly that invite. */
    /** A request that may bring `room`'s seat-fill key has gone out (`seatFillAccepting`). */
    const openSeatFillHold = (room: string): void => {
      seatFillRequestsOut += 1;
      seatFillHoldTimer ??= setTimeout(() => {
        seatFillHoldTimer = null;
        set({ seatFillAccepting: null });
      }, SEAT_FILL_ACCEPT_HOLD_MS);
      set({ seatFillAccepting: room });
    };
    /** …and has answered, AFTER leaving what it brought, so no render finds neither. */
    const closeSeatFillHold = (): void => {
      seatFillRequestsOut -= 1;
      if (seatFillRequestsOut > 0) return;
      if (seatFillHoldTimer) clearTimeout(seatFillHoldTimer);
      seatFillHoldTimer = null;
      set({ seatFillAccepting: null });
    };

    const dropIncomingInvite = (inviteId: string): void => {
      const s = get();
      invitesEpoch += 1;
      set({
        incomingInvites: s.incomingInvites.filter((i) => i.id !== inviteId),
        invitePrompt: s.invitePrompt?.id === inviteId ? null : s.invitePrompt,
      });
    };

    return {
      status: "idle",
      profile: null,
      sessionRecheckPending: false,
      error: null,
      // No error yet. `true` is the honest default for the one screen that reads it: its
      // `error ?? fa.status.error` fallback ends «دوباره تلاش کنید», so an unclassified failure
      // keeps the button it asks for.
      errorAction: "retry",
      view: "lobby",
      maintenance: null,
      channelGate: null,
      setMaintenance: ({ active, message }) =>
        set((s) =>
          // Only touch the store when the flag actually flips, so the success path (which
          // fires on EVERY response) never re-renders the tree.
          (s.maintenance?.active ?? false) === active &&
          (!active || s.maintenance?.message === message)
            ? s
            : { maintenance: active ? { active, message } : null },
        ),
      setChannelGate: (channels) =>
        set((s) => {
          // No-op unless the gate actually changes (success fires on every response).
          const now = channels && channels.length > 0 ? channels : null;
          const same =
            (s.channelGate === null && now === null) ||
            (s.channelGate?.length === now?.length &&
              (s.channelGate ?? []).every((c, i) => c.chatId === now?.[i]?.chatId));
          return same ? s : { channelGate: now };
        }),
      activeGame: "hearts",
      online: false,
      onlineJoin: QUICK_MATCH,
      liveRoomId: null,
      tableOnScreen: null,
      publicGameInProgress: false,
      initDataRaw: deps.initDataRaw ?? null,
      api: deps.api,
      chatPeer: null,
      viewedUserId: null,
      profileReturnView: "friends",

      stats: null,
      history: [],
      historyCursor: null,
      historyLoaded: false,
      historyLoading: false,
      historyError: null,
      activeGames: [],
      activeGamesLoading: false,

      friends: [],
      incoming: [],
      outgoing: [],
      incomingCount: 0,
      friendsLoading: false,
      friendsError: null,
      blocked: null,
      blockedLoading: false,
      blockedError: null,
      leaderboard: null,
      leaderboardLoading: false,
      leaderboardError: null,
      leaderboardReturn: null,
      dmUnread: {},
      dmUnreadTotal: 0,
      dmPreviews: {},
      dmPeers: {},
      dmBlocked: {},
      inbox: [],
      inboxUnread: 0,
      incomingInvites: [],
      outgoingInvites: [],
      invitePrompt: null,
      seatFillTicket: null,
      seatFillAccepting: null,
      gameSwitch: null,
      pendingInvite: null,
      lobbyGames: FALLBACK_LOBBY_GAMES,
      isAdmin: false,
      gameCounts: zeroGameCounts(),
      onlinePlayers: 0,
      resumeStripVersion: 0,
      presence: {},
      presenceLive: false,
      friendToast: null,
      friendToastInvite: null,
      friendToastNav: "friends",
      maintenanceWarning: null,
      dismissMaintenanceWarning: () => set({ maintenanceWarning: null }),

      navGuard: null,
      setNavGuard: (guard) => set({ navGuard: guard }),
      pageReturn: "lobby",
      setPageReturn: (view) => set({ pageReturn: view }),
      setView: (view) => {
        // A dirty screen's leave-guard can veto the nav (it then shows a confirm and
        // navigates itself). Any successful nav clears the guard so it can't leak.
        const guard = get().navGuard;
        if (guard && !guard(view)) return;
        // …and a PUBLIC TABLE IN PROGRESS is left in-app only through «خروج»'s own confirmation
        // (card-a5 item 1): park the destination; the table asks, and goes there if confirmed.
        const now = get();
        if (now.view === "game" && view !== "game" && now.publicGameInProgress) {
          set({ pendingExit: { to: view } });
          return;
        }
        set({ view, navGuard: null });
      },
      pendingExit: null,
      cancelTableExit: () => set({ pendingExit: null }),
      leaveTable: (view = "lobby") =>
        set({ view, navGuard: null, pendingExit: null, publicGameInProgress: false }),
      runExitIntent: (intent) => {
        // Applied directly, never through the guarded actions below: the table has just been left
        // (or could not be reached), so there is nothing left to route through.
        set({ pendingExit: null });
        switch (intent.kind) {
          case "solo":
            if (intent.resumeId !== undefined) setResumeSolo(intent.resumeId);
            set({ activeGame: intent.game, online: false, view: "game" });
            return;
          case "create": {
            const join: OnlineJoin = {
              kind: "create",
              settings: intent.settings,
              ...(intent.seats !== undefined ? { seats: intent.seats } : {}),
            };
            set((s) => ({
              activeGame: intent.game,
              online: true,
              onlineJoin: join,
              view: "game",
              // A Friends-screen pick bound to the routed rejoin this create replaces was made FOR this
              // create (`inviteFriendToGame` → `createPrivate` → `viaHeldTable`): it rides on to it, or the
              // friend is never invited (card-a5's final review, F1 — `pendingInvite` binds by identity).
              ...(s.pendingInvite !== null && s.pendingInvite.join === s.onlineJoin
                ? { pendingInvite: { ...s.pendingInvite, join } }
                : {}),
            }));
            return;
          }
          case "join":
            set({
              activeGame: intent.game,
              online: true,
              onlineJoin: {
                kind: "join",
                roomId: intent.roomId,
                ...(intent.preferredSeat !== undefined
                  ? { preferredSeat: intent.preferredSeat }
                  : {}),
              },
              view: "game",
            });
            return;
          case "resume":
            set({
              activeGame: intent.game,
              online: true,
              onlineJoin: { kind: "resume", onlineGameId: intent.onlineGameId },
              view: "game",
            });
            return;
          case "rejoin":
            set({
              activeGame: intent.game,
              online: true,
              onlineJoin: { kind: "rejoin", roomId: intent.roomId, token: intent.token },
              view: "game",
            });
            return;
          case "lobby":
            // The table's own «خروج» destination (card-9f) — exactly `leaveTable("lobby")`.
            get().leaveTable("lobby");
            return;
        }
      },
      // Solo path: selecting a game always resets to the offline (bot) room.
      setActiveGame: (id) => {
        if (heldPublicTable() !== null) {
          // A save named by the strip rides the intent — its one-shot must not outlive the detour.
          const resumeId = takeResumeSolo();
          takeResumeIntent();
          if (viaHeldTable({ kind: "solo", game: id, ...(resumeId ? { resumeId } : {}) })) return;
          if (resumeId) setResumeSolo(resumeId);
        }
        set({ activeGame: id, online: false });
      },
      playOnline: (id) =>
        set({ activeGame: id, online: true, onlineJoin: { kind: "quick" }, view: "game" }),
      setLiveRoom: (roomId) => set({ liveRoomId: roomId }),
      setTableOnScreen: (provider) => set({ tableOnScreen: provider }),
      releaseTableOnScreen: (provider) =>
        set((s) => (s.tableOnScreen === provider ? { tableOnScreen: null } : {})),
      setPublicGameInProgress: (value) => set({ publicGameInProgress: value }),
      openGameSettings: (id) => set({ activeGame: id, view: "gameSettings" }),
      createPrivate: (id, settings, seats) => {
        const sized = seats !== undefined ? { seats } : {};
        if (viaHeldTable({ kind: "create", game: id, settings, ...sized })) return;
        set({
          activeGame: id,
          online: true,
          onlineJoin: { kind: "create", settings, ...sized },
          view: "game",
        });
      },
      joinPrivate: (id, roomId, preferredSeat) => {
        if (
          viaHeldTable(
            {
              kind: "join",
              game: id,
              roomId,
              ...(preferredSeat !== undefined ? { preferredSeat } : {}),
            },
            roomId,
          )
        )
          return;
        set({
          activeGame: id,
          online: true,
          onlineJoin: {
            kind: "join",
            roomId,
            ...(preferredSeat !== undefined ? { preferredSeat } : {}),
          },
          view: "game",
        });
      },
      rejoinOnline: (game, roomId, token, opts) => {
        // Going back to the held table IS the return; any other table is an act elsewhere.
        if (
          opts?.auto === undefined &&
          viaHeldTable({ kind: "rejoin", game, roomId, token }, roomId)
        )
          return;
        set({
          activeGame: game,
          online: true,
          onlineJoin: {
            kind: "rejoin",
            roomId,
            token,
            ...(opts?.auto !== undefined ? { auto: opts.auto } : {}),
          },
          view: "game",
        });
      },
      returnToHeldSeat: (game, roomId, then) => {
        const known = refusalWayBack();
        set({
          activeGame: game,
          online: true,
          onlineJoin: {
            kind: "rejoin",
            roomId,
            token: known !== null && known.roomId === roomId ? known.token : null,
            ...(then !== undefined ? { then } : {}),
          },
          view: "game",
        });
      },
      returnToHeldTable: () => {
        const target = straightBackTarget();
        if (target === null) return false;
        get().rejoinOnline(target.game, target.roomId, target.token, {
          auto: target.waiting ? "waiting" : "table",
        });
        return true;
      },
      resumeOnlineGame: (game, onlineGameId) => {
        if (viaHeldTable({ kind: "resume", game, onlineGameId })) return;
        set({
          activeGame: game,
          online: true,
          onlineJoin: { kind: "resume", onlineGameId },
          view: "game",
        });
      },
      pendingFollowId: null,
      pendingChatPeerId: null,
      routeFromStartParam: (startParam) => {
        const follow = /^f_([a-z][a-z0-9]{15,40}|\d{5,19})$/.exec(startParam ?? "");
        if (follow?.[1]) {
          // The FOLLOW invite: boot continues to the lobby; the friend request to
          // the inviter fires after `loadProfile` (needs an authed session first).
          set({ pendingFollowId: follow[1] });
          return false;
        }
        // `c_<appUserId>` — the button on the "you have a new message" Telegram PV (sent
        // when a DM lands while the app is closed). Boot continues; the thread opens once
        // the friend list is in (we need the peer's FriendView to render it).
        const chat = /^c_([a-z0-9]{20,40})$/i.exec(startParam ?? "");
        if (chat?.[1]) {
          set({ pendingChatPeerId: chat[1] });
          return false;
        }
        const parsed = parseTableCode(startParam);
        // A hidden game's table link (an old `t_bluff_…`) is no route at all: boot lands in the lobby.
        if (!parsed || !ONLINE_GAME_IDS.has(parsed.gameId) || isHiddenGame(parsed.gameId)) {
          return false;
        }
        set({
          activeGame: parsed.gameId,
          online: true,
          // `fromLink` licenses the server's bounded invited-seat lookup: a `t_<game>_<roomId>`
          // deep link cannot carry the seat the inviter tapped, so without this a friend arriving
          // from the Telegram PV took the lowest free chair instead of the one they were invited
          // into (reported as «they joined as an opponent, not my partner»).
          onlineJoin: { kind: "join", roomId: parsed.roomId, fromLink: true },
          view: "game",
        });
        return true;
      },
      // Opening a thread clears its unread count locally right away; the DM
      // room persists the same clear (markRead on join), so it survives reload.
      openChat: (peer) => {
        set({ chatPeer: peer, view: "chat" });
        clearDmUnread(peer.userId);
      },
      setChatPeer: (peer) => {
        set({ chatPeer: peer });
        if (peer) clearDmUnread(peer.userId);
      },
      openProfileFromLeaderboard: (userId, at) => {
        set({ leaderboardReturn: at });
        get().openUserProfile(userId);
      },

      takeLeaderboardReturn: () => {
        const saved = get().leaderboardReturn;
        if (saved) set({ leaderboardReturn: null });
        return saved;
      },

      openUserProfile: (userId) => {
        // Remember where we came from so the back button returns there (Friends, the
        // table roster, etc.); the game/gameSettings views never open a profile.
        const from = get().view;
        set({
          viewedUserId: userId,
          profileReturnView: from === "userProfile" ? get().profileReturnView : from,
          view: "userProfile",
        });
      },

      loadProfile: async () => {
        if (profileInFlight) return; // a retry loop is already running — don't race it
        profileInFlight = true;
        set({ status: "loading", error: null });
        try {
          // Retry a TRANSIENT network blip a few times (with a real fetch timeout) before failing —
          // the fast path is unchanged (a healthy getMe returns on the first try). See resilient-boot.
          const profile = await resilientGetMe(deps.api.getMe);
          // `SelfProfile` embeds stats, so the Profile screen has them immediately;
          // `loadStats` can still refresh from the dedicated endpoint.
          set({ profile, stats: profile.stats, status: "ready" });
          afterProfileReady();
        } catch (error) {
          set({
            status: "error",
            error: bootErrorMessage(error),
            errorAction: bootErrorAction(error),
          });
        } finally {
          profileInFlight = false;
        }
      },

      refreshSession: async () => {
        // Not booted, or already on the boot error screen: nothing live is on screen, and the boot
        // path is exactly the screen-owning load it was before.
        if (get().status !== "ready") return get().loadProfile();
        if (sessionInFlight) {
          sessionAskedAgain = true;
          return;
        }
        sessionInFlight = true;
        try {
          for (;;) {
            sessionAskedAgain = false;
            try {
              // The api client mirrors a 503 / channel-gate 403 into `maintenance` / `channelGate`
              // as it would for any request; both screens yield to a live game by themselves.
              const profile = await resilientGetMe(deps.api.getMe);
              set({ profile, stats: profile.stats, sessionRecheckPending: false });
            } catch (error) {
              // ⚠️ AT A TABLE A REFUSAL WAITS: the error screen would replace — unmount — the game.
              // `view` is read HERE, when the answer lands, not when the push did: a player can sit
              // down while the read is out.
              if (get().view === "game") set({ sessionRecheckPending: true });
              // Elsewhere only a VERDICT WITH NO SCREEN OF ITS OWN takes the screen (a ban, a dead
              // session). Maintenance and the gate draw theirs off the flags the api client just set,
              // so the session stays «ready» — presence and the badge poll alive, which is what lets
              // maintenance OFF or a removed channel bring the app back (on «error» both are torn
              // down and nothing could). A transient failure is no verdict at all: nothing changes.
              else if (takesTheScreen(error))
                set({
                  status: "error",
                  error: bootErrorMessage(error),
                  errorAction: bootErrorAction(error),
                  sessionRecheckPending: false,
                });
              else set({ sessionRecheckPending: false });
            }
            if (!sessionAskedAgain || get().status !== "ready") break;
          }
        } finally {
          sessionInFlight = false;
        }
        // A push that arrived while THIS refusal took the screen (status «error» — never at a
        // table) is the boot path's to answer: the error screen's presence socket is gone, so
        // dropping it here would leave the player on it after maintenance ended.
        // ⚠️ ONLY from «error»: a full `loadProfile` from «ready» is the splash, i.e. the unmount.
        if (sessionAskedAgain && get().status !== "ready") {
          sessionAskedAgain = false;
          void get().loadProfile();
        }
      },

      saveProfile: async (input) => {
        const profile = await deps.api.updateProfile(input);
        set({ profile, stats: profile.stats });
      },

      checkUsername: (username) => deps.api.checkUsername(username),

      uploadAvatar: async (data, mime) => {
        // Retry a TRANSIENT failure before surfacing an error. The upload body is what these
        // networks sever mid-TLS (10.6% of prod attempts), and the api never even sees those — so
        // one glitch used to become one error toast, and the user retried by hand into the same
        // weather. A 4xx (413 too large / 400 undecodable) is NOT retried: it is a real verdict.
        const result = await resilientRequest(
          (init) => deps.api.uploadAvatar(data, mime, init),
          AVATAR_UPLOAD_RETRY,
        );
        const profile = get().profile;
        if (profile) set({ profile: { ...profile, avatarPhotoId: result.avatarPhotoId } });
        return result;
      },

      removeAvatarPhoto: async () => {
        await deps.api.removeAvatarPhoto();
        const profile = get().profile;
        if (profile) set({ profile: { ...profile, avatarPhotoId: null } });
      },

      updateNotifications: async (enabled) => {
        const profile = await deps.api.updateNotifications(enabled);
        set({ profile, stats: profile.stats });
      },

      loadStats: async () => {
        try {
          const stats = await deps.api.getStats();
          set({ stats });
        } catch {
          // Stats are non-critical (already seeded from the profile); ignore a refresh failure.
        }
      },

      loadHistory: async () => {
        set({ historyLoading: true, historyError: null });
        try {
          const page = await deps.api.getHistory({ limit: HISTORY_PAGE });
          set({
            history: page.items,
            historyCursor: page.nextCursor,
            historyLoaded: true,
            historyLoading: false,
          });
        } catch (error) {
          set({ historyLoading: false, historyError: messageOf(error) });
        }
      },

      loadMoreHistory: async () => {
        const { historyCursor, historyLoading, history } = get();
        if (!historyCursor || historyLoading) return;
        set({ historyLoading: true, historyError: null });
        try {
          const page = await deps.api.getHistory({ limit: HISTORY_PAGE, cursor: historyCursor });
          set({
            history: [...history, ...page.items],
            historyCursor: page.nextCursor,
            historyLoading: false,
          });
        } catch (error) {
          set({ historyLoading: false, historyError: messageOf(error) });
        }
      },

      loadActiveGames: async () => {
        set({ activeGamesLoading: true });
        try {
          const res = await deps.api.getActiveGames();
          set({ activeGames: res.games, activeGamesLoading: false });
        } catch {
          set({ activeGames: [], activeGamesLoading: false });
        }
      },

      clearActiveGame: (game) =>
        set({ activeGames: get().activeGames.filter((g) => g.game !== game) }),

      refreshFriends: coalesced(async () => {
        set({ friendsLoading: true });
        const epoch = friendsEpoch; // snapshot before the fetch
        try {
          const lists = await fetchFriendLists(deps.api);
          // NOTHING CHANGED — leave every slice exactly as it is. ⚠️ `friendsLoading` must still
          // be cleared, or a 304 would leave a spinner up for ever.
          if (isUnchanged(lists)) {
            set({ friendsLoading: false, friendsError: null });
            return;
          }
          const { friends, incoming, outgoing } = lists;
          // A friend mutation landed WHILE this poll was in flight → its slices are newer than
          // this (older) server snapshot; don't clobber them. Just clear loading (the next poll,
          // issued after the mutation, reconciles).
          if (friendsEpoch !== epoch) {
            // ⚠️ …AND FORGET THIS BODY'S ETAG, or «the next poll reconciles» is false. The client
            // remembered it before we saw the body, so keeping it makes the next read a 304
            // against data this store never applied — and that read is usually the trailing run
            // queued for a change the dropped body already carried (`conditional-drop-race`).
            deps.api.forgetConditional?.("/friends/all");
            set({ friendsLoading: false, friendsError: null });
            return;
          }
          set({
            friends,
            incoming,
            outgoing,
            incomingCount: incoming.length,
            friendsLoading: false,
            friendsError: null,
          });
        } catch (error) {
          set({ friendsLoading: false, friendsError: messageOf(error) });
        }
      }),

      requestFriend: async (userId) => {
        const view = await deps.api.requestFriend(userId);
        friendsEpoch += 1;
        set({ outgoing: upsert(get().outgoing, view) });
        return view;
      },

      acceptFriend: async (userId) => {
        const view = await deps.api.acceptFriend(userId);
        const s = get();
        const incoming = without(s.incoming, userId);
        friendsEpoch += 1;
        set({ incoming, incomingCount: incoming.length, friends: upsert(s.friends, view) });
        return view;
      },

      rejectFriend: async (userId) => {
        const view = await deps.api.rejectFriend(userId);
        const incoming = without(get().incoming, userId);
        friendsEpoch += 1;
        set({ incoming, incomingCount: incoming.length });
        return view;
      },

      cancelFriend: async (userId) => {
        const view = await deps.api.cancelFriend(userId);
        friendsEpoch += 1;
        set({ outgoing: without(get().outgoing, userId) });
        return view;
      },

      removeFriend: async (userId) => {
        const view = await deps.api.removeFriend(userId);
        friendsEpoch += 1;
        set({ friends: without(get().friends, userId) });
        return view;
      },

      blockUser: async (userId) => {
        const view = await deps.api.blockFriend(userId);
        // A block replaces whatever relationship existed, so the user leaves
        // every list (friends AND any pending request row).
        const s = get();
        const incoming = without(s.incoming, userId);
        friendsEpoch += 1;
        set({
          friends: without(s.friends, userId),
          incoming,
          incomingCount: incoming.length,
          outgoing: without(s.outgoing, userId),
        });
        return view;
      },

      unblockUser: async (userId) => {
        // Blocked users never sit in the friends/incoming/outgoing lists, so there is
        // nothing to reconcile THERE — but «کاربران مسدود» is exactly the list they DO sit
        // in, and it is not on any poll, so it has to be dropped here or the row lingers
        // until the screen is reopened.
        const view = await deps.api.unblockFriend(userId);
        const blocked = get().blocked;
        if (blocked) set({ blocked: blocked.filter((u) => u.userId !== userId) });
        return view;
      },

      loadBlocked: async () => {
        set({ blockedLoading: true });
        try {
          const blocked = await deps.api.listBlocked();
          set({ blocked, blockedLoading: false, blockedError: null });
        } catch (error) {
          // Keep whatever was already on screen; a refresh that fails must not blank a
          // list the user is reading. `blocked` stays null on a FIRST failure, which is
          // what tells the screen to show the error instead of the empty line.
          set({ blockedLoading: false, blockedError: messageOf(error) });
        }
      },

      /* A monotonically increasing token: only the NEWEST request may write. Switching period or
       * game twice quickly otherwise lets the first response land last and paint the wrong board. */
      loadLeaderboard: async (period, game, opts = {}) => {
        const mine = ++leaderboardEpoch;
        if (!opts.quiet) set({ leaderboardLoading: true });
        try {
          const board = await deps.api.leaderboard(period, game);
          if (mine !== leaderboardEpoch) return;
          set({ leaderboard: board, leaderboardLoading: false, leaderboardError: null });
        } catch (error) {
          if (mine !== leaderboardEpoch) return;
          // Keep whatever is on screen; a refresh that fails must not blank a board being read.
          set({ leaderboardLoading: false, leaderboardError: messageOf(error) });
        }
      },

      refreshDmUnread: coalesced(async () => {
        const epoch = dmUnreadEpoch; // snapshot before the fetch
        try {
          // dm-summary is the dm-unread superset: counts (0 allowed) PLUS the
          // last-message preview per conversation, in one fetch.
          const res = await deps.api.getDmSummary();
          // Nothing moved since the last read — the counts, previews and peers all still stand.
          if (isUnchanged(res)) return;
          // A live `dm_message` push (or an open-thread clear) landed WHILE this poll was in
          // flight → this server snapshot predates it and would drop that increment. Skip it; the
          // next poll (issued after the push) carries the truth — so this body's ETag must go
          // too, or that poll is a 304 against a body never applied: the push carries no
          // preview, so the last message would stay the previous one (see `refreshFriends`).
          if (dmUnreadEpoch !== epoch) {
            deps.api.forgetConditional?.("/me/dm-summary");
            return;
          }
          const dmUnread: Record<string, number> = {};
          const dmPreviews: Record<string, DmLastMessage> = {};
          const dmPeers: Record<string, InviteUser> = {};
          const dmBlocked: Record<string, true> = {};
          for (const t of res.threads) {
            if (t.unread > 0) dmUnread[t.peerId] = t.unread;
            if (t.last) dmPreviews[t.peerId] = t.last;
            if (t.peer) dmPeers[t.peerId] = t.peer;
            if (t.blockedByMe) dmBlocked[t.peerId] = true;
          }
          // Server truth REPLACES local state — it clears rows read on another
          // device and re-syncs anything a missed push under-counted. But keep
          // the OPEN thread at zero: its room join may still be persisting the
          // markRead this fetch raced with.
          const openPeer = get().chatPeer?.userId;
          if (openPeer) delete dmUnread[openPeer];
          set({ dmUnread, dmUnreadTotal: sumUnread(dmUnread), dmPreviews, dmPeers, dmBlocked });
        } catch {
          // Best-effort: keep the last-known counts; the poll retries.
        }
      }),

      hideDmThread: async (peerId) => {
        // Persist the one-sided hide first, then drop the row locally.
        await deps.api.hideDmThread(peerId);
        const s = get();
        const dmUnread = { ...s.dmUnread };
        delete dmUnread[peerId];
        const dmPreviews = { ...s.dmPreviews };
        delete dmPreviews[peerId];
        // Bump the dm epoch so an in-flight refreshDmUnread (carrying pre-hide
        // server state) can't resurrect the row — same guard as clearDmUnread.
        dmUnreadEpoch += 1;
        set({ dmUnread, dmUnreadTotal: sumUnread(dmUnread), dmPreviews });
      },

      loadInbox: async () => {
        // Opening the Messages screen shows the list AND marks everything read (it's a
        // broadcast feed — nothing to leave unread once seen).
        // ⚠️ ALWAYS A FULL READ. The badge poll (`refreshInboxUnread`) reads the SAME `/me/inbox`
        // and keeps only `unread`: it throws `messages` away while the client keeps that body's
        // ETag, so a conditional read here was answered 304 against a list this store never held —
        // the screen rendered `[]`, zeroed the badge and marked nothing read. Forgetting the tag on
        // THIS side keeps the poll's 304s; the screen opens rarely, so its bytes do not matter.
        deps.api.forgetConditional?.("/me/inbox");
        // A 304 can therefore only come from a client with no `forgetConditional` (a test fake):
        // the list we hold IS the list then — render it, and never treat it as «no messages».
        const res = await deps.api.getInbox();
        const messages = isUnchanged(res) ? get().inbox : res.messages;
        set({ inbox: messages, inboxUnread: 0 });
        if (messages.some((m) => !m.read)) {
          try {
            await deps.api.markAllInboxRead();
            set({ inbox: get().inbox.map((m) => ({ ...m, read: true })) });
          } catch {
            // Best-effort: the list still shows; unread re-syncs on the next poll.
          }
        }
      },

      refreshInboxUnread: coalesced(async () => {
        try {
          const res = await deps.api.getInbox();
          if (isUnchanged(res)) return; // the badge is already right
          set({ inboxUnread: res.unread });
        } catch {
          // Best-effort: keep the last-known count; the poll retries.
        }
      }),

      deleteInboxMessage: async (id) => {
        // Optimistic: drop the card immediately, then persist. Restore on failure so the
        // list never silently lies about what the server holds.
        const before = get().inbox;
        const removed = before.find((m) => m.id === id);
        set({
          inbox: before.filter((m) => m.id !== id),
          inboxUnread:
            removed && !removed.read ? Math.max(0, get().inboxUnread - 1) : get().inboxUnread,
        });
        try {
          await deps.api.deleteInboxMessage(id);
        } catch (err) {
          set({ inbox: before });
          throw err;
        }
      },

      clearInbox: async () => {
        const before = get().inbox;
        set({ inbox: [], inboxUnread: 0 });
        try {
          await deps.api.clearInbox();
        } catch (err) {
          set({ inbox: before });
          throw err;
        }
      },

      refreshInvites: async () => {
        const epoch = invitesEpoch; // snapshot before the fetch
        try {
          const [incomingInvites, fetchedOutgoing] = await Promise.all([
            deps.api.listIncomingInvites(),
            deps.api.listOutgoingInvites(),
          ]);
          // The server lists only LIVE (pending) invites. Merge, don't replace,
          // the outgoing side: locally-known rows it omits stay — the terminal
          // states (accepted/declined/expired/canceled, pushed live) that drive
          // the waiting-room chips, and a just-created invite a raced stale
          // fetch would otherwise clobber.
          // A live invite push (new incoming / a response drop) landed WHILE this fetch was in
          // flight → don't clobber the incoming side with this older snapshot (the outgoing side is
          // already merge-not-replace, so it's safe). The next refresh reconciles.
          if (invitesEpoch !== epoch) return;
          const kept = get().outgoingInvites.filter(
            (local) => !fetchedOutgoing.some((o) => o.id === local.id),
          );
          // ANCHOR the incoming side against THIS device's clock as it lands (see
          // `game/invite-clock.ts`): every later «is it still live» / countdown then compares two
          // readings of one clock, so a phone that disagrees with the server can no longer make
          // every invite look expired the moment it arrives.
          const anchored = anchorInvites(incomingInvites, get().incomingInvites);
          // AN OPEN PROMPT TRACKS THE LIST — it holds a SNAPSHOT, and the server can move an
          // invite's deadline under it. A lone host's invite is created with the open
          // `GAME_INVITE_MAX_TTL_MS` window and SHORTENED to the table's own window the moment its
          // fill countdown arms; the invitee has no channel of its own to hear that. Left alone,
          // its card would count down from ten minutes over an invite that is already dead — a
          // پیوستن that looks live and 409s when tapped, which is worse than no prompt at all.
          // Re-pointing costs nothing (same id, same identities, fresher deadline) and lets the
          // card's own expiry notice fire at the moment the server actually lets go.
          // ⚠️ ABSENT rows are deliberately LEFT ALONE rather than expired here: an accept flips
          // the row out of the live list, and racing this refresh against it would expire the card
          // under a player who is mid-tap. A genuinely withdrawn invite arrives as `invite_canceled`.
          const open = get().invitePrompt;
          const fresh = open ? anchored.find((i) => i.id === open.id) : undefined;
          set({
            incomingInvites: anchored,
            outgoingInvites: [...fetchedOutgoing, ...kept],
            ...(fresh ? { invitePrompt: fresh } : {}),
          });
        } catch {
          // Invites are best-effort here: live pushes and the next open re-sync.
        }
      },

      sendGameInvite: async (toUserId, game, roomId, opts) => {
        const invite = await deps.api.createInvite({
          toUserId,
          game,
          roomId,
          // Each field is omitted rather than sent as `undefined`: the body is zod-parsed
          // server-side and an explicit `undefined` key is not the same as an absent one.
          ...(opts?.seat !== undefined ? { seat: opts.seat } : {}),
          ...(opts?.reconnectToken !== undefined ? { reconnectToken: opts.reconnectToken } : {}),
        });
        set({ outgoingInvites: upsertInvite(get().outgoingInvites, invite) });
        return invite;
      },

      acceptGameInvite: async (inviteId) => {
        try {
          const res = await deps.api.acceptInvite(inviteId);
          dropIncomingInvite(inviteId);
          const { game, roomId, seat, reconnectToken } = res.joinTarget;
          if (seat != null && reconnectToken) {
            // MID-GAME seat-fill: the room is locked, so DON'T joinById — reconnect with the
            // minted token straight into the reserved bot chair (reuses the same-device rejoin path).
            get().rejoinOnline(game, roomId, reconnectToken);
          } else {
            // Ordinary invite → enter the table exactly like a `t_<game>_<roomId>` deep link would,
            // carrying the invited SEAT as a preference when the inviter named one (the public
            // «+»). No token, so nothing is held: if that chair filled meanwhile, the server seats
            // them in the lowest free one instead.
            get().joinPrivate(game, roomId, seat ?? undefined);
          }
          return res;
        } catch (error) {
          // Expired / already decided: the invite is dead — drop its row, but
          // leave the prompt up so the caller can show the mapped Persian reason
          // in place (the prompt dismisses itself right after).
          if (
            error instanceof ApiError &&
            (error.code === "INVITE_EXPIRED" || error.code === "INVITE_NOT_PENDING")
          ) {
            set({ incomingInvites: get().incomingInvites.filter((i) => i.id !== inviteId) });
          }
          throw error;
        }
      },

      declineGameInvite: async (inviteId) => {
        try {
          await deps.api.declineInvite(inviteId);
        } catch (error) {
          // An expired/decided invite is already gone — the user's intent (make it
          // go away) is satisfied. Anything else is a real failure: keep the row
          // (so a retry is possible) and rethrow.
          const alreadyDead =
            error instanceof ApiError &&
            (error.code === "INVITE_EXPIRED" || error.code === "INVITE_NOT_PENDING");
          if (!alreadyDead) throw error;
        }
        dropIncomingInvite(inviteId);
      },

      cancelGameInvite: async (inviteId) => {
        try {
          const invite = await deps.api.cancelInvite(inviteId);
          set({ outgoingInvites: upsertInvite(get().outgoingInvites, invite) });
          return invite;
        } catch (error) {
          // THE INVITE ALREADY SETTLED (the friend accepted — joining IS accepting — or it
          // declined/expired). The server returns 409 INVITE_NOT_PENDING / INVITE_EXPIRED. The
          // local copy is still PENDING, so the OnlineGame `rosterFull`/`waiting→playing` effects
          // (which read the list fresh) re-fire this doomed cancel on every re-render, and the
          // host's «لغو دعوت» chip never clears. Reconcile: the invite is no longer actionable, so
          // drop it from the outgoing list — the chip reads a PENDING row, so removing it clears
          // the chip AND stops the retry loop. Any OTHER failure is genuinely transient (retryable),
          // so it still rejects and the row is left untouched.
          if (
            error instanceof ApiError &&
            (error.code === "INVITE_NOT_PENDING" || error.code === "INVITE_EXPIRED")
          ) {
            const settled = get().outgoingInvites.find((i) => i.id === inviteId);
            // Drop the settled invite from the outgoing list — it is no longer a pending invite,
            // so the «لغو دعوت» chip (which filters on PENDING) clears and the effects stop retrying.
            // A bare 409 does not say WHICH terminal it reached (ACCEPTED / DECLINED / EXPIRED), so
            // we do not invent one — no caller reads this return value; it exists only to satisfy
            // the Promise<GameInviteView> contract. Re-throw when we had no such row to reconcile.
            if (!settled) throw error;
            set({ outgoingInvites: get().outgoingInvites.filter((i) => i.id !== inviteId) });
            return settled;
          }
          throw error;
        }
      },

      inviteFriendToGame: (toUserId, game) => {
        get().createPrivate(game, DEFAULT_TABLE_SETTINGS);
        // Bound to THAT create's own intent object — see `pendingInvite`.
        set({ pendingInvite: { toUserId, join: get().onlineJoin } });
      },

      clearPendingInvite: () => set({ pendingInvite: null }),

      // THE TAP ON A `game_invite` TOAST — AND IT MUST ALWAYS LAND SOMEWHERE.
      //
      // The toast is raised synchronously and unconditionally, while the prompt sits behind a
      // network round-trip whose failure is swallowed. That asymmetry is what let a player be TOLD
      // about an invite with no way to answer it. So: if the invite is not in the list, do not
      // shrug — refetch once and try again, and if it is genuinely gone SAY so instead of
      // silently doing nothing.
      openInvitePrompt: (inviteId) => {
        const show = (): boolean => {
          const s = get();
          const invite = s.incomingInvites.find((i) => i.id === inviteId);
          if (!invite || !isLiveInvite(invite)) return false;
          if (invite.roomId === joinedRoomId(s)) return true; // already AT that table — nothing to ask
          set({ invitePrompt: invite });
          return true;
        };
        if (show()) return;
        void (async () => {
          await get().refreshInvites();
          if (show()) return;
          set({ friendToast: fa.invite.unavailable, friendToastInvite: null });
        })();
      },

      dismissInvitePrompt: () => set({ invitePrompt: null }),

      beginGameSwitch: (message) => {
        gameSwitchAt = Date.now();
        if (gameSwitchMaxTimer) clearTimeout(gameSwitchMaxTimer);
        // Safety net: if the invited game never reaches a resolved phase (stuck connect), don't
        // trap the user under the overlay forever — drop it after GAME_SWITCH_MAX_MS.
        gameSwitchMaxTimer = setTimeout(() => {
          gameSwitchMaxTimer = null;
          set({ gameSwitch: null });
        }, GAME_SWITCH_MAX_MS);
        set({ gameSwitch: { message } });
      },

      clearGameSwitch: () => {
        if (!get().gameSwitch) return;
        const elapsed = Date.now() - gameSwitchAt;
        const finish = (): void => {
          if (gameSwitchMaxTimer) {
            clearTimeout(gameSwitchMaxTimer);
            gameSwitchMaxTimer = null;
          }
          set({ gameSwitch: null });
        };
        // Minimum-visible hold so an instant (cached) connect doesn't flash the transition.
        if (elapsed >= GAME_SWITCH_MIN_MS) finish();
        else setTimeout(finish, GAME_SWITCH_MIN_MS - elapsed);
      },

      promptNextIncomingInvite: () => {
        const s = get();
        if (s.invitePrompt) return;

        // NEVER PROMPT FOR THE TABLE WE ARE ALREADY WALKING INTO.
        //
        // Tapping «ورود به میز بازی» in the Telegram invite opens the app on
        // `?startapp=t_<game>_<roomId>`, which joins that table directly — that tap IS the
        // acceptance. But the invite row is still PENDING, so this sweep used to prompt on
        // top of the game the user had just joined: «yes» ran `acceptGameInvite` →
        // `joinPrivate` → a SECOND join that tore down the live connection ("clicking yes
        // or no breaks the joining"), and «no» declined an invite they had just accepted,
        // telling the inviter they refused while they sat at the table.
        //
        // So settle it silently instead: accept it server-side (idempotent — it is the same
        // call the prompt would make, minus the re-join), which also tells the inviter they
        // joined, and drop it from the list.
        //
        // …AND KEEP THE KEY IT COMES BACK WITH (card-c1). A MID-GAME seat-fill's answer carries the
        // token `acceptGameInvite` reconnects with; the `joinById` already racing is refused by the
        // dealt table, and the token is what walks past that lock, so it goes to that join's ticket
        // door. A fresh mint kills the older tokens and the list is newest first, so an older row
        // never replaces a newer row's token, whichever answer lands first.
        const room = joinedRoomId(s);
        // ⚠️ Wrapped, not passed by reference: `filter` hands the callback the INDEX as its second
        // argument, which would land in `isLiveInvite`'s `now` parameter and make every invite
        // look live forever. Caught by the «a spent invite is still never prompted» control.
        const live = s.incomingInvites.filter((i) => isLiveInvite(i));
        if (room !== null) {
          const here = live.filter((i) => i.roomId === room);
          let keptRank = here.length;
          here.forEach((invite, rank) => {
            const seatFill = invite.seat != null;
            if (seatFill) openSeatFillHold(room);
            void deps.api
              .acceptInvite(invite.id)
              .then(({ joinTarget: { roomId, seat, reconnectToken } }) => {
                if (seat == null || !reconnectToken || rank > keptRank) return;
                keptRank = rank;
                set({ seatFillTicket: { roomId, token: reconnectToken } });
              })
              .catch(() => undefined) // best-effort
              .finally(() => {
                if (seatFill) closeSeatFillHold();
              });
            dropIncomingInvite(invite.id);
          });
        }

        const next = live.find((i) => i.roomId !== room);
        if (next) set({ invitePrompt: next });
      },

      sweepInvites: () => {
        // ⚠️ Read before the fetch: the table's own join started in this same tick on a slow boot, and
        // its refusal can beat the list. The prompt below re-reads it, for the table of the answer.
        const room = joinedRoomId(get());
        if (room !== null) openSeatFillHold(room);
        void get()
          .refreshInvites()
          .then(() => get().promptNextIncomingInvite())
          .finally(() => {
            if (room !== null) closeSeatFillHold();
          });
      },

      takeSeatFillTicket: (roomId) => {
        const ticket = get().seatFillTicket;
        if (ticket?.roomId !== roomId) return null;
        set({ seatFillTicket: null });
        return ticket.token;
      },

      loadGames: async () => {
        if (gamesLoading) return;
        gamesLoading = true;
        try {
          const res = await deps.api.getGames();
          // Server truth wins over the static fallback; tiles render strictly
          // ascending by the canonical order index. A hidden game is dropped whatever its status
          // and whoever asks — see `FALLBACK_LOBBY_GAMES`.
          const lobbyGames = res.games
            .filter((entry) => !isHiddenGame(entry.game))
            .sort((a, b) => a.order - b.order);
          if (lobbyGames.length > 0) set({ lobbyGames, isAdmin: res.isAdmin });
          // Feed the deployment's real bot into the invite deep-link builder.
          setBotUsername(res.botUsername);
        } catch {
          // Keep the last-known list (or the fallback) — the server-side join
          // gate stays authoritative even when this read fails.
        } finally {
          gamesLoading = false;
        }
      },

      connectPresence: () => {
        const connector = deps.presenceConnector;
        if (!connector || presenceHandle || presenceConnecting) return;
        presenceConnecting = true;
        const gen = ++presenceGen;
        if (presenceAttempts === 0) presenceFirstAttemptAt = Date.now();
        presenceAttempts++;
        // Track FOREGROUND from the lifecycle's fused, debounced activity signal (the same
        // one that drives reconnection), and report every flip. Subscribed once per connect;
        // the singleton is inert until `start()`, so this never fires in tests.
        offForeground ??= lifecycle.subscribe((s) => {
          if (s.appActive === wantForeground) return; // the store also emits on overlay changes
          wantForeground = s.appActive;
          presenceHandle?.setForeground(s.appActive);
        });
        void connector({
          onSnapshot: (entries) => {
            const presence: Record<string, PresenceStatus> = {};
            for (const e of entries) presence[e.userId] = e.status;
            set({ presence });
          },
          onPresence: (update) => {
            const presence = { ...get().presence };
            if (update.status === "offline") delete presence[update.userId];
            else presence[update.userId] = update.status;
            set({ presence });
          },
          onFriendEvent: (push) => {
            // Reconcile the affected slice per kind, then toast (when the kind
            // has a line — `new_follower` and unknown kinds are ignored).
            switch (push.type) {
              case "friend_request":
              case "friend_accept":
                // Live request/accept: reconcile the lists (badge + rows).
                void get().refreshFriends();
                break;
              case "dm_message": {
                // A DM landed while its thread ISN'T open here (the room only
                // publishes then) → bump the sender's unread count. If the
                // thread IS open (race: push beat the room's mark-read, or the
                // toast arrived as the user opened it), the count stays 0.
                const s = get();
                const fromId = push.actor.userId;
                if (!(s.view === "chat" && s.chatPeer?.userId === fromId)) {
                  const dmUnread = { ...s.dmUnread, [fromId]: (s.dmUnread[fromId] ?? 0) + 1 };
                  dmUnreadEpoch += 1; // guard: an in-flight refreshDmUnread must not drop this
                  set({ dmUnread, dmUnreadTotal: sumUnread(dmUnread) });
                }
                // The push carries no body — pull the fresh last-message preview
                // (the room publishes AFTER persisting, so the summary sees it).
                void get().refreshDmUnread();
                break;
              }
              case "game_invite": {
                // Pull the authoritative view (full identities), then surface the
                // pushed invite as the app-level prompt if it is still live.
                const inviteId = push.invite?.id;
                void get()
                  .refreshInvites()
                  .then(() => {
                    if (!inviteId) return;
                    const invite = get().incomingInvites.find(
                      (i) => i.id === inviteId && isLiveInvite(i),
                    );
                    if (invite) {
                      set({ invitePrompt: invite });
                      // This push refetched within a second of the invite being created, so the
                      // gap to its server `createdAt` IS this device's clock offset. It used to
                      // decide, silently, whether the player could answer at all; report a big one
                      // so the population is countable instead of arriving as a support message.
                      const skewMs = deviceClockSkewMs(invite);
                      if (Math.abs(skewMs) >= CLOCK_SKEW_REPORT_MS) {
                        reportClientFault("invite-clock-skew", { skewMs: Math.round(skewMs) });
                      }
                      return;
                    }
                    // Told about an invite we cannot offer. The toast below still fires and its
                    // tap now repairs itself, but this must never be silent again.
                    reportClientFault("invite-prompt-missing", {
                      invite: inviteId,
                      known: get().incomingInvites.length,
                    });
                  });
                break;
              }
              case "invite_canceled":
                // The inviter withdrew it — drop it live (prompt included).
                if (push.invite) dropIncomingInvite(push.invite.id);
                break;
              case "game_replaced":
                // The owner filled the seat I'd left (but could still reclaim) with a friend:
                // the match is over for me. Clear the saved online game (matching guard → a
                // NEWER game survives) and bump the strip version so an open lobby drops the
                // «ادامه بازی» row live. The toast below says «جایگزین شدی».
                clearOnlineGame(push.room?.roomId);
                set({ resumeStripVersion: get().resumeStripVersion + 1 });
                break;
              case "invite_accepted":
              case "invite_declined":
              case "invite_expired": {
                // I am the inviter: flip my outgoing row to its terminal state.
                const id = push.invite?.id;
                const status =
                  push.type === "invite_accepted"
                    ? "ACCEPTED"
                    : push.type === "invite_declined"
                      ? "DECLINED"
                      : "EXPIRED";
                if (id)
                  set({
                    outgoingInvites: get().outgoingInvites.map((i) =>
                      i.id === id ? { ...i, status } : i,
                    ),
                  });
                break;
              }
            }
            const toast = friendEventToast(push);
            if (toast !== null) {
              set({
                friendToast: toast,
                friendToastInvite: push.type === "game_invite" ? (push.invite?.id ?? null) : null,
                friendToastNav: push.type === "dm_message" ? "chat" : "friends",
              });
            }
          },
          onGameCounts: (counts, online) => {
            // Zero-fill defensively (the server already sends every key) so a
            // partial payload can never leave a stale per-game figure behind. `snake` is
            // never zero-filled: a push without it (an older game-server) clears it.
            set({ gameCounts: { ...zeroGameCounts(), ...counts }, onlinePlayers: online });
          },
          onMaintenance: (warning) => {
            // Pre-deploy heads-up fanned out to every in-app user → pop the app-wide apology
            // modal (App.tsx renders it while this is non-null). Purely a mirror of the push.
            set({ maintenanceWarning: warning });
          },
          onDataChanged: ({ scope, user, all }) => {
            // A PEER'S IDENTITY CHANGED — patch that ONE entry, in place, in silence.
            //
            // ⚠️ IT DELIBERATELY DOES NOT REFETCH. Re-reading `/friends/all` to change one row's
            // name would be a full 200 (the body moved) for every online friend of whoever
            // renamed themselves, and it would replace the whole list — every row a new object,
            // every row re-rendered. This rewrites one entry wherever it appears and leaves the
            // array identity of everything else alone.
            //
            // ⚠️ AND IT IS SILENT BY CONSTRUCTION: no toast, no sound, no loading flag. It is not
            // a notification — nobody asked to be told that a friend changed their avatar.
            //
            // ⚠️ IT DOES NOT TOUCH A LIVE TABLE. A game names its seats from the LOBBY FRAME off
            // the game socket (`seatName(lobby.players, seat)`), never from these lists, so a
            // rename mid-match cannot move a name on the felt — which is what the operator asked
            // for, and is a property of where the data comes from rather than of this code.
            if (scope === "profile" && user) {
              const patch = <T extends { userId: string }>(rows: T[]): T[] => {
                let hit = false;
                const next = rows.map((r) => {
                  if (r.userId !== user.id) return r;
                  hit = true;
                  return { ...r, ...user, userId: user.id };
                });
                return hit ? next : rows; // untouched arrays keep their identity → no re-render
              };
              const s0 = get();
              const friends = patch(s0.friends);
              const incoming = patch(s0.incoming);
              const outgoing = patch(s0.outgoing);
              const peer = s0.dmPeers[user.id];
              const dmPeers = peer
                ? { ...s0.dmPeers, [user.id]: { ...peer, ...user, userId: user.id } }
                : s0.dmPeers;
              if (
                friends !== s0.friends ||
                incoming !== s0.incoming ||
                outgoing !== s0.outgoing ||
                dmPeers !== s0.dmPeers
              ) {
                set({ friends, incoming, outgoing, dmPeers });
              }
              return;
            }
            // «That slice moved» — re-read exactly that route and nothing else. Each read is
            // ETag-conditional, so a push that turns out to change nothing this client can see
            // (a friend mutation between two OTHER people is not one of these, but a racing
            // double push is) costs a 304 with no body.
            //
            // ⚠️ A FAN-OUT IS SPREAD; A TARGETED PUSH NEVER IS. `all` means the presence room
            // broadcast this to EVERY connected socket (an in-app broadcast, or a maintenance /
            // channel-gate toggle), so without a spread ~300 clients re-read the SAME route in
            // the same millisecond against a single-threaded api — and a 304 costs the server
            // exactly what a 200 does, because the ETag is computed in `onSend` AFTER the
            // handler has run. The ceiling is derived in `badge-poll.ts` and sits far inside the
            // 20 s tick that carried these before: a broadcast badge measured 3,479 ms on the
            // old build. Everything else — a DM, a friend request, a read on another device —
            // stays instant, which is the whole point of the batch.
            const run = (fn: () => void): void => {
              if (!all) {
                fn();
                return;
              }
              window.setTimeout(fn, Math.random() * BROADCAST_RESYNC_SPREAD_MS);
            };
            if (scope === "friends") run(() => void get().refreshFriends());
            else if (scope === "dm") run(() => void get().refreshDmUnread());
            else if (scope === "inbox") run(() => void get().refreshInboxUnread());
            // `session` is NOT a badge: the caller's own standing changed (banned/unbanned, or
            // maintenance/channel-gate toggled). Re-read `/me`, whose 403/503 renders the banned
            // screen, the channel gate and the maintenance screen — IN THE BACKGROUND: this used
            // to be `loadProfile`, whose «loading» swapped a live table for the boot splash and
            // so closed the seat on purpose (see `refreshSession`).
            else if (scope === "session") run(() => void get().refreshSession());
          },
          onLive: (live) => {
            // The ONLY writer of `presenceLive`. App.tsx keys the poll's cadence on it: live ⇒
            // a slow safety net, down ⇒ the old fast interval, because with no socket there are
            // no pushes at all and the poll is the only thing left.
            if (get().presenceLive !== live) set({ presenceLive: live });
          },
          onError: () => {
            // Presence is best-effort; the poll remains the fallback.
          },
        })
          .then((handle) => {
            presenceConnecting = false;
            if (gen !== presenceGen) {
              handle.leave(); // superseded by a teardown/reconnect — drop it
              return;
            }
            presenceHandle = handle;
            // ⚠️ `presenceLive` IS **NOT** SET HERE, AND THAT IS THE POINT.
            //
            // It used to be, because the initial join emits no `onStatus` (the lifecycle reports
            // TRANSITIONS and a first connect is not one) — but «the socket opened» is the wrong
            // question. A socket opens perfectly well against a game-server that cannot push
            // anything at all: one from before the `data-events` batch, one with no Redis, or the
            // one you get back after a game-server-only rollback. Asserting liveness here would
            // drop the badge poll to its 120 s net while NOTHING is being pushed.
            // The connector answers the right question instead — it calls `onLive` off the join
            // SNAPSHOT, which carries `dataEvents` — so this stays false until that lands, a few
            // milliseconds later, and stays false for ever against a server that cannot push.
            // Sync activity set before the connect landed (deep-link into a game).
            if (wantActivity.inGame) handle.setInGame(true, wantActivity.game);
            // …and a foreground flip that happened while connecting. Only the BACKGROUND case
            // needs sending: a fresh join already means foreground, server-side.
            if (!wantForeground) handle.setForeground(false);
          })
          .catch(() => {
            presenceConnecting = false;
            // The join never landed — stay on the fast fallback.
            set({ presenceLive: false });
            // …and try again, unless the session tore presence down meanwhile or the budget is spent.
            if (gen !== presenceGen) return;
            const spent = Date.now() - presenceFirstAttemptAt;
            if (
              presenceAttempts >= MAX_RECONNECT_ATTEMPTS ||
              spent >= RECONNECT_WINDOW_MS + SERVER_GRACE_MARGIN_MS
            ) {
              presenceAttempts = 0;
              return;
            }
            presenceRetry = setTimeout(
              () => get().connectPresence(),
              backoffDelayMs(presenceAttempts),
            );
          });
      },

      disconnectPresence: () => {
        presenceGen++; // invalidate any in-flight connect
        presenceConnecting = false;
        clearTimeout(presenceRetry);
        presenceAttempts = 0;
        // No socket ⇒ no pushes ⇒ the fast fallback poll is the only thing keeping badges alive.
        set({ presenceLive: false });
        wantActivity = { inGame: false, game: null };
        offForeground?.();
        offForeground = null;
        wantForeground = true;
        presenceHandle?.leave();
        presenceHandle = null;
        set({
          presence: {},
          gameCounts: zeroGameCounts(),
          onlinePlayers: 0,
          friendToast: null,
          friendToastInvite: null,
          friendToastNav: "friends",
        });
      },

      setInGame: (inGame, game = null) => {
        // The game tag only means something while in-game; normalise it away
        // otherwise so a stale id can never ride a "left the table" ping.
        wantActivity = { inGame, game: inGame ? game : null };
        presenceHandle?.setInGame(wantActivity.inGame, wantActivity.game);
      },

      dismissFriendToast: () =>
        set({ friendToast: null, friendToastInvite: null, friendToastNav: "friends" }),
    };
  });
  // THE DEFERRED REFUSAL IS ASKED AGAIN THE MOMENT THE PLAYER LEAVES THE TABLE — by whatever door
  // (the end card, «خروج», a back gesture): a subscription, because `view` has many writers and a
  // re-check wired into one of them is a re-check the others skip. The server's answer THEN decides
  // (an unban or maintenance off in between leaves nothing to show).
  store.subscribe((state, prev) => {
    if (prev.view === "game" && state.view !== "game" && state.sessionRecheckPending) {
      void state.refreshSession();
    }
  });
  // NOBODY IS STUCK AFTER MAINTENANCE OFF (operator, 2026-10-04).
  //
  // A session that BOOTED into maintenance (503 on `/me` ⇒ «error» + the flag) has no presence
  // socket — presence connects only on «ready» — so the OFF push can never reach it: the player sat
  // on the maintenance screen until they reopened the app. So from the moment a session that is not
  // «ready» meets maintenance until it IS «ready», ask `/me` every {@link MAINTENANCE_OFF_POLL_MS}
  // and the moment the app returns to the foreground (a suspended WebView fires no timers). Not for every
  // boot error: a ban or a dead session is the same answer however often it is asked. It runs from
  // the STORE, so it also carries a player sitting at a table they returned to from that screen.
  // A «ready» session (maintenance met by a background re-read) needs none of it: its presence
  // socket is alive and the OFF push reaches it.
  //
  // ⚠️ THE WAIT IS A LATCH, NOT THE FLAG (review of 9536160). ANY 2xx/304 clears the flag — not only
  // `/me` — so after OFF the first call to land can be somebody else's: the table-status read a
  // refused rejoin makes, a roster friend request, a report. Keyed on the flag, the poll tore itself
  // down right there and left the session «error» for good — the maintenance text on the error
  // screen, with a retry button, once the player left the table. So the wait starts when a session
  // that is not «ready» meets maintenance, and ends only at «ready» (or at a verdict the poll itself
  // met, above). And a flag that falls while we wait is news in itself — somebody just got an
  // answer — so it is asked at once rather than at the next tick (a no-op while an ask is out, which
  // is what the poll's own successful `/me` looks like from here).
  store.subscribe((state, prev) => {
    if (state.status === "ready") offWait = false;
    else if (state.maintenance?.active) offWait = true;
    if (offWait && prev.maintenance?.active && !state.maintenance?.active) {
      void probeMaintenanceOff();
    }
    const wanted = offWait;
    if (wanted === (offPollTimer !== null)) return; // armed once; a later write re-fires this
    if (wanted) {
      offPollTimer = setInterval(() => void probeMaintenanceOff(), MAINTENANCE_OFF_POLL_MS);
      let wasActive = lifecycle.appActive;
      offPollForeground = lifecycle.subscribe((l) => {
        if (l.appActive === wasActive) return;
        wasActive = l.appActive;
        if (l.appActive) void probeMaintenanceOff();
      });
      return;
    }
    if (offPollTimer !== null) clearInterval(offPollTimer);
    offPollTimer = null;
    offPollForeground?.();
    offPollForeground = null;
  });
  // A SAVE THE SERVER JUST RETIRED (made under rules the game no longer plays; deleted, recorded as
  // nothing) leaves «ادامه بازی» AT ONCE. The solo connector reports the refusal from either door —
  // the «ادامه بازی» join or a reconnect's re-join — and the list is re-read while the player is
  // still reading the notice, so the card is gone before they are back in the lobby to tap it.
  // Never unsubscribed: the app makes ONE store for its life (a test's every store listens on).
  onSavedGameRetired(() => void store.getState().loadActiveGames());
  return store;
}

export type SessionStore = ReturnType<typeof createSessionStore>;
